// ─── App.jsx ──────────────────────────────────────────────────────────────────
// nuclear-duck-adintel / Vercel frontend
//
// CHANGE LOG:
// ── 2026-03-31 ──────────────────────────────────────────────────────────────
// [1] ADDED: Executive Summary block — renders after report header, before
//     strategic hook. Displays category_truth, customer_expectation,
//     brand_positioning, the_gap, the_opportunity from report.executive_summary.
//     Styled with .execSummary / .execSummaryHeader / .execSummaryBody / .execLabel
//     CSS classes. Renders in both React UI and HTML export (generateReportHTML).
//
// [2] CHANGED: core_strategic_misalignment label — "WHY THIS CREATES CAMPAIGN
//     FAILURE" renamed to "WHERE THIS LEAVES PERFORMANCE ON THE TABLE".
//     Field reads mis.why_this_matters first, falls back to mis.why_this_creates_failure
//     for backward compatibility with older reports.
//
// [3] CHANGED: HTML export (generateReportHTML) — executive summary now renders
//     as correct HTML template literal after r-cover div, before hook section.
//     CSS classes r-exec-summary / r-exec-summary-label / r-exec-label added
//     to the <style> block inside the HTML export.
//
// [4] ADDED: "HOW CUSTOMERS THINK ABOUT THIS CATEGORY" section — renders after
//     Executive Summary, before Strategic Hook. Three-part structure:
//     category_setup (1-2 sentences), customer_questions (4-6 real PAA questions),
//     intent_translation (1-2 sentences). Sourced from category_thinking field.
//     CSS classes .categoryThinking* for React UI, .r-cat-thinking* for HTML export.
//
// ── 2026-04-01 ──────────────────────────────────────────────────────────────
// [5] ADDED: Modular report type selector in the download actions row.
//     reportType state: "full" (default) | "adangles"
//     Toggle appears after report generates. Controls which HTML/PDF is exported.
//     Full report flow completely unchanged when reportType === "full".
//
// [6] ADDED: generateAdAngleReportHTML() — standalone Ad Angle Report generator.
//     Reads recommended_ad_angles from existing report JSON.
//     Groups by funnel stage. Shows hook, what_it_addresses, suggested_usage,
//     proof_direction per angle. Backward compatible with old angle shape (angle/why/tied_to).
//     Does not affect generateReportHTML() in any way.
//
// [7] UPDATED: handleDownloadHTML and handleDownloadPDF now branch on reportType.
//     No new handlers needed — existing buttons drive both report types.
// ─────────────────────────────────────────────────────────────────────────────

import { useState, useEffect } from "react";
import {
  safeList,
  normalizeText,
  normaliseCustomerLanguage,
  formatThemeLabel,
  normaliseAdAngle,
  ACTIVITY_PHRASES,
  downloadHTML,
  generateNavBar,
  generateAdAngleReportHTML,
  generateMessageGapReportHTML,
  generateCustomerSignalsReportHTML,
  generatePortalHTML,
  generateReportHTML,
  generateExecutiveSummaryHTML,
} from "./lib/generators.js";

// ─── STYLES ───────────────────────────────────────────────────────────────────
const css = `
  @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Mono:wght@300;400;500&family=Inter:wght@300;400;500;600;700&display=swap');
  * { box-sizing: border-box; }
  html, body, #root { margin: 0; min-height: 100%; background: #f8f9fb; }
  body { color: #1E2D4A; font-family: 'Inter', sans-serif; }
  :root {
    --bg: #f8f9fb; --surface: #ffffff; --border: #e2e8f0;
    --blue: #2B7FD4; --blue-dark: #1E2D4A; --blue-light: #6FAED9;
    --blue-dim: rgba(43,127,212,0.08); --blue-mid: rgba(43,127,212,0.15);
    --muted: #374151; --mono: 'DM Mono', monospace; --display: 'Bebas Neue', sans-serif;
    --red: #DC2626; --red-dim: rgba(220,38,38,0.07);
  }
  .app { min-height: 100vh; background: var(--bg); }
  .topbar { border-bottom: 1px solid var(--border); display: flex; justify-content: space-between; gap: 24px; padding: 16px 32px; position: sticky; top: 0; z-index: 5; background: rgba(255,255,255,0.95); backdrop-filter: blur(10px); box-shadow: 0 1px 3px rgba(30,45,74,0.06); }
  .brand { display: flex; gap: 12px; align-items: center; }
  .brandMark { width: 38px; height: 38px; background: var(--blue-dark); color: #fff; display: flex; align-items: center; justify-content: center; font-family: var(--display); font-size: 1.1rem; letter-spacing: 0.06em; }
  .brandTitle { font-family: var(--display); font-size: 1.3rem; letter-spacing: 0.1em; color: var(--blue-dark); }
  .brandSub { font-family: var(--mono); font-size: 0.55rem; letter-spacing: 0.16em; color: var(--blue); }
  .topMeta { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
  .tag { border: 1px solid var(--border); padding: 6px 10px; font-family: var(--mono); font-size: 0.55rem; letter-spacing: 0.14em; color: var(--muted); background: #fff; }
  .tag.live { border-color: rgba(43,127,212,0.35); color: var(--blue); background: var(--blue-dim); }
  .wrap { max-width: 1120px; margin: 0 auto; padding: 48px 24px 72px; }
  .hero { margin-bottom: 28px; }
  .eyebrow { font-family: var(--mono); font-size: 0.6rem; letter-spacing: 0.22em; color: var(--blue); margin-bottom: 12px; }
  .headline { font-family: var(--display); font-size: clamp(3rem, 7vw, 5.75rem); line-height: 0.94; margin: 0 0 14px 0; letter-spacing: 0.03em; color: var(--blue-dark); }
  .headline em { font-style: normal; color: var(--blue); }
  .subhead { max-width: 760px; color: var(--muted); line-height: 1.75; font-size: 0.95rem; }
  .panel { border: 1px solid var(--border); background: var(--surface); margin-top: 28px; box-shadow: 0 1px 4px rgba(30,45,74,0.06); }
  .panelBar { padding: 12px 18px; border-bottom: 1px solid var(--border); background: var(--blue-dim); display: flex; gap: 10px; align-items: center; }
  .dot { width: 8px; height: 8px; background: var(--blue); border-radius: 999px; }
  .panelBarText { font-family: var(--mono); font-size: 0.58rem; letter-spacing: 0.18em; color: var(--blue); }
  .panelBody { padding: 22px; }
  .grid2 { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
  .field { display: flex; flex-direction: column; gap: 7px; }
  .label { font-family: var(--mono); font-size: 0.58rem; letter-spacing: 0.16em; color: #6b7280; }
  .input { width: 100%; border: 1px solid var(--border); background: #fff; color: var(--blue-dark); padding: 12px 14px; outline: none; font-family: var(--mono); font-size: 0.82rem; transition: border-color 0.15s; }
  .input:focus { border-color: var(--blue); box-shadow: 0 0 0 3px rgba(43,127,212,0.1); }
  .brandCombo { position: relative; }
  .brandMatches { position: absolute; top: calc(100% + 2px); left: 0; right: 0; z-index: 20; max-height: 220px; overflow-y: auto; background: #fff; border: 1px solid var(--border); box-shadow: 0 4px 12px rgba(30,45,74,0.10); }
  .brandMatch { padding: 9px 14px; font-family: var(--mono); font-size: 0.8rem; color: var(--blue-dark); cursor: pointer; }
  .brandMatch:hover, .brandMatch.active { background: var(--blue-dim); color: var(--blue); }
  .brandMatch.addnew { color: var(--blue); border-top: 1px solid var(--border); }
  .checkboxGrid { display: grid; grid-template-columns: repeat(2, minmax(180px, 1fr)); gap: 10px; margin-top: 8px; }
  .checkboxItem { border: 1px solid var(--border); background: #fff; padding: 10px 12px; display: flex; gap: 8px; align-items: center; font-family: var(--mono); font-size: 0.72rem; color: var(--blue-dark); cursor: pointer; transition: border-color 0.15s, background 0.15s; }
  .checkboxItem:hover { border-color: var(--blue); background: var(--blue-dim); }
  .checkboxItem.disabled { opacity: 0.45; cursor: not-allowed; background: var(--bg); }
  .checkboxItem.disabled:hover { border-color: var(--border); background: var(--bg); }
  .checkboxHint { font-family: var(--mono); font-size: 0.55rem; letter-spacing: 0.1em; color: #9ca3af; margin-left: 6px; text-transform: none; }
  .runBtn { margin-top: 18px; border: none; background: var(--blue-dark); color: #fff; padding: 14px 26px; font-family: var(--display); letter-spacing: 0.14em; font-size: 1.04rem; cursor: pointer; transition: background 0.15s; }
  .runBtn:hover { background: var(--blue); }
  .runBtn:disabled { opacity: 0.45; cursor: not-allowed; }
  /* Outlined build button. Kept its outlined treatment from when it sat second
     in a pair; the margin-left that spaced it off GENERATE CLUSTERS is gone with
     that button, so it aligns with the fields above it. */
  .runBtnAlt { background: transparent; color: var(--blue-dark); border: 1px solid var(--blue-dark); }
  .runBtnAlt:hover { background: var(--blue-dim); }
  .chainDone { border: 1px solid var(--border); border-left: 3px solid var(--blue-dark); background: #fff; padding: 26px 28px; margin-top: 22px; }
  .chainDoneKicker { font-family: var(--mono); font-size: 0.58rem; letter-spacing: 0.16em; color: var(--blue); }
  .chainDoneTitle { font-family: var(--display); font-size: 1.5rem; letter-spacing: 0.04em; margin: 8px 0 12px; }
  .chainDoneBody { font-size: 0.92rem; line-height: 1.6; color: var(--muted); max-width: 620px; }
  .chainDoneCtx { display: flex; align-items: center; gap: 10px; margin-top: 16px; flex-wrap: wrap; }
  .chainDoneCtxLabel { font-family: var(--mono); font-size: 0.55rem; letter-spacing: 0.14em; color: #9ca3af; }
  .chainDoneCtxVal { font-family: var(--mono); font-size: 0.8rem; background: var(--bg); border: 1px solid var(--border); padding: 5px 9px; user-select: all; word-break: break-all; }
  .chainDoneNext { font-size: 0.86rem; line-height: 1.6; color: var(--muted); margin-top: 16px; max-width: 620px; }
  .chainDoneBtn { display: inline-block; text-decoration: none; }
  .recentCtx { margin-top: 18px; border-top: 1px solid var(--border); padding-top: 12px; }
  .recentCtxLabel { font-family: var(--mono); font-size: 0.55rem; letter-spacing: 0.14em; color: #9ca3af; margin-bottom: 8px; }
  .recentCtxRow { display: flex; align-items: baseline; gap: 12px; padding: 5px 0; text-decoration: none; color: inherit; border-bottom: 1px solid var(--border); }
  .recentCtxRow:last-child { border-bottom: 0; }
  .recentCtxRow:hover .recentCtxId { color: var(--blue); }
  .recentCtxDate { font-size: 0.78rem; color: var(--muted); }
  .recentCtxId { font-family: var(--mono); font-size: 0.72rem; color: #9ca3af; margin-left: auto; }
  .formAdv { border: 1px solid var(--border); background: #fff; margin-top: 14px; }
  .formAdvHead { display: flex; align-items: center; justify-content: space-between; padding: 12px 16px; cursor: pointer; background: var(--bg); transition: background 0.15s; user-select: none; }
  .formAdvHead:hover { background: var(--blue-dim); }
  .formAdvTitle { font-family: var(--mono); font-size: 0.58rem; letter-spacing: 0.16em; color: var(--blue); display: flex; align-items: center; gap: 10px; }
  .formAdvCount { font-family: var(--mono); font-size: 0.5rem; letter-spacing: 0.1em; color: #fff; background: var(--blue); padding: 2px 7px; text-transform: none; }
  .formAdvChevron { font-family: var(--mono); font-size: 0.7rem; color: #6b7280; }
  .formAdvBody { padding: 16px; border-top: 1px solid var(--border); }
  .formAdv { border: 1px solid var(--border); background: #fff; margin-top: 14px; }
  .formAdvHead { display: flex; align-items: center; justify-content: space-between; padding: 12px 16px; cursor: pointer; background: var(--bg); transition: background 0.15s; user-select: none; }
  .formAdvHead:hover { background: var(--blue-dim); }
  .formAdvTitle { font-family: var(--mono); font-size: 0.58rem; letter-spacing: 0.16em; color: var(--blue); display: flex; align-items: center; gap: 10px; }
  .formAdvCount { font-family: var(--mono); font-size: 0.5rem; letter-spacing: 0.1em; color: #fff; background: var(--blue); padding: 2px 7px; text-transform: none; }
  .formAdvChevron { font-family: var(--mono); font-size: 0.7rem; color: #6b7280; }
  .formAdvBody { padding: 16px; border-top: 1px solid var(--border); }
  .error { margin-top: 14px; color: #dc2626; font-family: var(--mono); font-size: 0.72rem; }
  .report { margin-top: 30px; }
  .reportHead { display: grid; grid-template-columns: 1fr auto; gap: 22px; border: 1px solid var(--border); background: var(--blue-dark); padding: 26px 28px; }
  .reportKicker { font-family: var(--mono); color: var(--blue-light); font-size: 0.58rem; letter-spacing: 0.2em; margin-bottom: 10px; }
  .reportTitle { font-family: var(--display); font-size: clamp(2.2rem, 5vw, 3.6rem); letter-spacing: 0.04em; line-height: 0.96; margin: 0; color: #fff; }
  .reportMeta { font-family: var(--mono); color: rgba(255,255,255,0.6); font-size: 0.6rem; letter-spacing: 0.1em; margin-top: 10px; }
  .categoryBox { text-align: right; min-width: 220px; }
  .categoryLabel { font-family: var(--mono); color: rgba(255,255,255,0.6); font-size: 0.58rem; letter-spacing: 0.14em; margin-bottom: 6px; }
  .categoryValue { font-family: var(--display); font-size: 2rem; letter-spacing: 0.04em; color: var(--blue-light); line-height: 1; }
  .summaryStrip { background: var(--blue); color: #fff; padding: 16px 22px; font-weight: 600; line-height: 1.7; font-size: 0.92rem; }
  .actionsRow { display: flex; gap: 12px; flex-wrap: wrap; margin: 18px 0 8px; align-items: center; }
  .actionBtn { border: 1px solid var(--blue); background: #fff; color: var(--blue); padding: 11px 18px; font-family: var(--mono); font-size: 0.7rem; letter-spacing: 0.12em; cursor: pointer; transition: background 0.15s, color 0.15s; display: flex; align-items: center; gap: 8px; }
  .actionBtn:hover { background: var(--blue); color: #fff; }
  .actionBtn:disabled { opacity: 0.45; cursor: not-allowed; }
  /* ── REPORT TYPE TOGGLE ── */
  .reportTypeToggle { display: flex; border: 1px solid var(--blue); overflow: hidden; }
  .reportTypeBtn { border: none; padding: 11px 18px; font-family: var(--mono); font-size: 0.7rem; letter-spacing: 0.12em; cursor: pointer; transition: background 0.15s, color 0.15s; background: #fff; color: var(--blue); }
  .reportTypeBtn + .reportTypeBtn { border-left: 1px solid var(--blue); }
  .reportTypeBtn.active { background: var(--blue); color: #fff; }
  .reportTypeBtn:hover:not(.active) { background: var(--blue-dim); }
  @keyframes spin { to { transform: rotate(360deg); } }
  .spinner { width: 13px; height: 13px; border: 2px solid currentColor; border-top-color: transparent; border-radius: 50%; animation: spin 0.7s linear infinite; display: inline-block; flex-shrink: 0; }
  .loadingBox { margin-top: 24px; border: 1px solid var(--border); background: var(--surface); box-shadow: 0 1px 4px rgba(30,45,74,0.06); overflow: hidden; }
  .loadingHeader { padding: 20px 24px 16px; border-bottom: 1px solid var(--border); }
  .loadingKicker { font-family: var(--mono); font-size: 0.55rem; letter-spacing: 0.22em; color: var(--blue); margin-bottom: 8px; }
  .loadingTitle { font-family: var(--display); font-size: 1.9rem; letter-spacing: 0.06em; margin: 0; color: var(--blue-dark); line-height: 1; }
  .loadingTitle span { color: var(--blue); }
  .loadingSteps { display: grid; }
  .loadingStep { display: flex; align-items: center; gap: 14px; padding: 12px 24px; border-bottom: 1px solid var(--border); background: #fff; transition: background 0.2s; }
  .loadingStep:last-child { border-bottom: none; }
  .loadingStep.ls-done { background: #fff; }
  .loadingStep.ls-active { background: rgba(43,127,212,0.04); border-left: 2px solid var(--blue); padding-left: 22px; }
  .loadingStep.ls-idle { background: #fff; }
  .stepIndicator { width: 22px; height: 22px; flex-shrink: 0; display: flex; align-items: center; justify-content: center; }
  .stepDotIdle { width: 9px; height: 9px; border-radius: 50%; background: var(--border); }
  .stepDotDone { width: 9px; height: 9px; border-radius: 50%; background: var(--blue); }
  .stepDotActive { position: relative; width: 22px; height: 22px; display: flex; align-items: center; justify-content: center; }
  .stepPulseRing { position: absolute; width: 32px; height: 32px; border-radius: 50%; background: rgba(245,158,11,0.3); animation: pulseRing 1.2s ease-out infinite; }
  .stepPulseRing2 { animation-delay: 0.4s; }
  .stepDotCore { width: 9px; height: 9px; border-radius: 50%; background: var(--blue); position: relative; z-index: 1; }
  @keyframes pulseRing { 0% { transform: scale(0.45); opacity: 0.9; } 100% { transform: scale(1); opacity: 0; } }
  .stepNum { font-family: var(--mono); font-size: 0.5rem; letter-spacing: 0.14em; color: #9ca3af; min-width: 20px; }
  .stepLabel { font-family: var(--mono); font-size: 0.68rem; letter-spacing: 0.06em; color: #9ca3af; flex: 1; transition: color 0.2s; }
  .loadingStep.ls-done .stepLabel { color: var(--blue-dark); }
  .loadingStep.ls-active .stepLabel { color: var(--blue-dark); font-weight: 500; }
  .stepCheck { font-size: 0.65rem; color: var(--blue); opacity: 0; transition: opacity 0.2s; }
  .loadingStep.ls-done .stepCheck { opacity: 1; }
  .loadingBar { padding: 14px 24px 16px; border-top: 1px solid var(--border); display: flex; align-items: center; gap: 14px; }
  .loadingBarLabel { font-family: var(--mono); font-size: 0.52rem; letter-spacing: 0.16em; color: #6b7280; white-space: nowrap; }
  .loadingBarTrack { flex: 1; height: 2px; background: rgba(43,127,212,0.12); }
  .loadingBarFill { height: 100%; background: var(--blue); transition: width 0.45s ease; }
  @keyframes ellipsis { 0%{content:''} 25%{content:'.'} 50%{content:'..'} 75%{content:'...'} 100%{content:''} }
  .loadingStep.ls-active .stepLabel::after { content: ''; animation: ellipsis 1.2s steps(1) infinite; color: #F59E0B; font-weight: 900; font-size: 1rem; letter-spacing: 2px; }
  .loadingStep.ls-active .stepLabel { color: var(--blue-dark); font-weight: 600; letter-spacing: 0.08em; }
  @keyframes shimmer { 0%{opacity:1} 50%{opacity:0.6} 100%{opacity:1} }
  .loadingBarFill { height: 100%; background: var(--blue); transition: width 0.45s ease; animation: shimmer 1.8s ease-in-out infinite; }
  .loadingBarPct { font-family: var(--mono); font-size: 0.52rem; letter-spacing: 0.1em; color: var(--blue); min-width: 32px; text-align: right; }
  .section { margin-top: 16px; border: 1px solid var(--border); background: var(--surface); box-shadow: 0 1px 3px rgba(30,45,74,0.04); }
  .sectionHead { padding: 14px 18px; border-bottom: 1px solid var(--border); background: var(--blue-dim); display: flex; align-items: center; justify-content: space-between; gap: 10px; cursor: pointer; }
  .sectionTitle { font-family: var(--display); font-size: 1.1rem; letter-spacing: 0.12em; color: var(--blue-dark); }
  .sectionToggle { font-family: var(--mono); font-size: 0.62rem; color: #6b7280; }
  .sectionBody { padding: 18px; }
  .bullets { display: grid; gap: 10px; }
  .bullet { border-left: 3px solid var(--blue); background: var(--blue-dim); padding: 12px 14px; line-height: 1.7; color: var(--blue-dark); font-size: 0.9rem; }
  .breakdownDataGrid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; margin-bottom: 22px; }
  .breakdownStatBox { border: 1px solid var(--border); background: #fff; padding: 16px 14px; text-align: center; }
  .breakdownStatLabel { font-family: var(--mono); color: #6b7280; font-size: 0.52rem; letter-spacing: 0.18em; margin-bottom: 10px; text-transform: uppercase; }
  .breakdownStatValue { font-family: var(--display); font-size: 2.4rem; color: var(--blue); line-height: 1; }
  .breakdownThreeCol { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 14px; margin-top: 4px; }
  .breakdownBlock { border: 1px solid var(--border); background: #fff; padding: 14px; }
  .breakdownBlockLabel { font-family: var(--mono); color: var(--blue); font-size: 0.52rem; letter-spacing: 0.18em; margin-bottom: 10px; text-transform: uppercase; }
  .breakdownItem { padding: 7px 0; border-bottom: 1px solid var(--border); font-size: 0.82rem; line-height: 1.55; color: var(--muted); }
  .breakdownItem:last-child { border-bottom: none; }
  .clusterList { display: grid; gap: 12px; }
  .clusterCard { border: 1px solid var(--border); background: #fff; padding: 16px; display: grid; gap: 14px; border-left: 3px solid var(--blue); box-shadow: 0 1px 3px rgba(30,45,74,0.04); }
  .clusterTop { display: flex; justify-content: space-between; gap: 16px; align-items: start; }
  .clusterName { font-family: var(--display); font-size: 1.35rem; letter-spacing: 0.04em; color: var(--blue-dark); }
  .clusterStage { font-family: var(--mono); color: var(--blue); font-size: 0.66rem; letter-spacing: 0.08em; white-space: nowrap; border: 1px solid rgba(43,127,212,0.35); background: var(--blue-dim); padding: 5px 8px; }
  .miniLabel { font-family: var(--mono); color: #6b7280; font-size: 0.55rem; letter-spacing: 0.16em; margin-bottom: 5px; }
  .miniCopy { line-height: 1.65; color: var(--muted); font-size: 0.82rem; }
  .queryList { display: grid; gap: 7px; margin-top: 4px; }
  .queryPill { border: 1px solid var(--border); background: var(--bg); padding: 9px 10px; font-size: 0.8rem; line-height: 1.5; color: var(--blue-dark); }
  .signalPill { border: 1px solid rgba(43,127,212,0.25); background: var(--blue-dim); padding: 9px 10px; font-size: 0.8rem; line-height: 1.5; color: var(--blue); font-style: italic; }
  .patternBox { border-left: 2px solid var(--blue-light); padding: 10px 14px; background: var(--blue-dim); font-size: 0.82rem; line-height: 1.65; color: var(--muted); }
  .whyBox { border: 1px solid rgba(43,127,212,0.3); background: var(--blue-dim); padding: 12px 14px; font-size: 0.85rem; line-height: 1.65; color: var(--blue-dark); font-weight: 600; }
  .whyLabel { font-family: var(--mono); color: var(--blue); font-size: 0.52rem; letter-spacing: 0.2em; margin-bottom: 6px; }
  .sourceList { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 4px; }
  .sourceBadge { font-family: var(--mono); font-size: 0.55rem; letter-spacing: 0.12em; padding: 4px 8px; border: 1px solid var(--border); color: #6b7280; background: var(--bg); }
  .signals { display: grid; grid-template-columns: repeat(auto-fill, minmax(170px, 1fr)); gap: 10px; }
  .signalBox { border: 1px solid var(--border); background: #fff; padding: 12px; }
  .signalLabel { font-family: var(--mono); color: #6b7280; font-size: 0.55rem; letter-spacing: 0.15em; margin-bottom: 6px; }
  .signalValue { font-size: 0.82rem; color: var(--blue-dark); line-height: 1.5; word-break: break-word; }
  .evidenceList { display: grid; gap: 10px; margin-top: 12px; }
  .evidenceCard { border: 1px solid var(--border); background: #fff; padding: 14px; }
  .evidenceTop { display: flex; justify-content: space-between; gap: 12px; align-items: start; margin-bottom: 8px; }
  .evidenceType { font-family: var(--mono); font-size: 0.56rem; letter-spacing: 0.16em; color: var(--blue); }
  .evidenceTitle { font-weight: 700; color: var(--blue-dark); line-height: 1.4; margin-bottom: 6px; }
  .evidenceSnippet { color: var(--muted); line-height: 1.6; font-size: 0.82rem; }
  .evidenceLink { color: var(--blue); text-decoration: underline; font-size: 0.78rem; word-break: break-all; }
  .evidenceBadge { font-family: var(--mono); font-size: 0.55rem; padding: 4px 8px; border: 1px solid rgba(43,127,212,0.35); background: var(--blue-dim); color: var(--blue); white-space: nowrap; }
  .marketInsightBox { border: 1px solid rgba(43,127,212,0.2); background: var(--blue-dim); padding: 18px 22px; margin-top: 16px; }
  .marketInsightLabel { font-family: var(--mono); color: var(--blue); font-size: 0.52rem; letter-spacing: 0.22em; margin-bottom: 8px; }
  .marketInsightText { font-size: 0.9rem; line-height: 1.75; color: var(--muted); }
  .coverageGrid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; margin-bottom: 14px; }
  .coverageBox { border: 1px solid var(--border); background: #fff; padding: 14px; text-align: center; }
  .coverageNum { font-family: var(--display); font-size: 2rem; color: var(--blue); line-height: 1; margin-bottom: 4px; }
  .coverageLabel { font-family: var(--mono); font-size: 0.5rem; letter-spacing: 0.16em; color: #6b7280; text-transform: uppercase; }
  .sourceTagList { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 12px; }
  .sourceTag { font-family: var(--mono); font-size: 0.58rem; letter-spacing: 0.1em; padding: 6px 12px; border: 1px solid rgba(43,127,212,0.35); background: var(--blue-dim); color: var(--blue); }
  .languageSection { display: grid; gap: 18px; }
  .languageTheme { border: 1px solid var(--border); background: #fff; overflow: hidden; }
  .languageThemeLabel { font-family: var(--mono); font-size: 0.52rem; letter-spacing: 0.18em; color: var(--blue); padding: 8px 14px; background: var(--blue-dim); border-bottom: 1px solid var(--border); text-transform: uppercase; }
  .languageGrid { display: grid; gap: 6px; padding: 12px 14px; }
  .languageQuote { border-left: 3px solid var(--blue-light); background: #fff; padding: 10px 14px; font-size: 0.84rem; line-height: 1.6; color: var(--blue-dark); font-style: italic; border: 1px solid var(--border); border-left: 3px solid var(--blue-light); }
  .adAnglesBlock { margin-top: 16px; border: 2px solid var(--blue); background: var(--surface); overflow: hidden; box-shadow: 0 1px 4px rgba(43,127,212,0.1); }
  .adAnglesHeader { background: var(--blue); padding: 14px 20px; }
  .adAnglesHeaderText { font-family: var(--display); font-size: 1.1rem; letter-spacing: 0.12em; color: #fff; }
  .adAnglesSubtext { font-family: var(--mono); font-size: 0.52rem; letter-spacing: 0.14em; color: rgba(255,255,255,0.7); margin-top: 2px; }
  .adAnglesList { display: grid; gap: 0; }
  .adAnglesItem { padding: 16px 20px; border-bottom: 1px solid var(--border); background: #fff; transition: background 0.15s; }
  .adAnglesItem:last-child { border-bottom: none; }
  .adAnglesItem:hover { background: var(--blue-dim); }
  .adAnglesItemTop { display: flex; align-items: start; gap: 14px; }
  .adAnglesNum { font-family: var(--display); font-size: 1.4rem; color: var(--blue); line-height: 1; min-width: 28px; opacity: 0.4; flex-shrink: 0; }
  .adAnglesText { font-size: 0.96rem; font-weight: 600; color: var(--blue-dark); line-height: 1.45; flex: 1; }
  .adAnglesWhy { font-family: var(--mono); font-size: 0.6rem; color: #6b7280; margin-top: 6px; margin-left: 42px; line-height: 1.5; }
  .adAnglesTiedTo { display: inline-block; font-family: var(--mono); font-size: 0.52rem; letter-spacing: 0.1em; padding: 3px 8px; border: 1px solid rgba(43,127,212,0.3); background: var(--blue-dim); color: var(--blue); margin-top: 4px; margin-left: 42px; }
  /* ── BRAND POSITIONING ── */
  .brandPosBlock { margin-top: 4px; border: 1px solid var(--border); background: #fff; overflow: hidden; }
  .brandPosHeader { background: var(--blue-dark); padding: 12px 18px; display: flex; align-items: center; justify-content: space-between; }
  .brandPosHeaderText { font-family: var(--display); font-size: 1.05rem; letter-spacing: 0.12em; color: #fff; }
  .brandPosGrid { display: grid; grid-template-columns: 1fr 1fr; border-bottom: 1px solid var(--border); }
  .brandPosCol { padding: 16px 18px; }
  .brandPosCol:first-child { border-right: 1px solid var(--border); }
  .brandPosColLabel { font-family: var(--mono); font-size: 0.52rem; letter-spacing: 0.18em; text-transform: uppercase; color: var(--blue); margin-bottom: 10px; }
  .brandPosItem { padding: 8px 0; border-bottom: 1px solid var(--border); font-size: 0.84rem; line-height: 1.55; color: var(--blue-dark); }
  .brandPosItem:last-child { border-bottom: none; }
  .brandPosSiteQuote { font-family: var(--mono); font-size: 0.78rem; font-style: italic; color: var(--blue); padding: 6px 10px; background: var(--blue-dim); border: 1px solid rgba(43,127,212,0.2); margin-bottom: 6px; }
  .brandPosSiteQuote:last-child { margin-bottom: 0; }
  .brandPosValue { padding: 14px 18px; background: var(--bg); border-bottom: 1px solid var(--border); font-size: 0.86rem; line-height: 1.7; color: var(--muted); }
  .brandPosValueLabel { font-family: var(--mono); font-size: 0.5rem; letter-spacing: 0.16em; text-transform: uppercase; color: #6b7280; margin-bottom: 6px; }
  .brandPosInterpret { padding: 14px 18px; background: var(--red-dim); border-top: 2px solid var(--red); }
  .brandPosInterpretLabel { font-family: var(--mono); font-size: 0.5rem; letter-spacing: 0.16em; text-transform: uppercase; color: var(--red); margin-bottom: 10px; }
  .brandPosInterpretTags { display: flex; flex-wrap: wrap; gap: 8px; }
  .brandPosInterpretTag { font-family: var(--mono); font-size: 0.72rem; font-style: italic; color: var(--red); padding: 5px 12px; border: 1px solid rgba(220,38,38,0.3); background: #fff; }
  /* ── CATEGORY EXPECTATIONS ── */
  .catExpBlock { margin-top: 4px; border: 1px solid var(--border); background: #fff; overflow: hidden; }
  .catExpHeader { background: var(--blue-dark); padding: 12px 18px; }
  .catExpHeaderText { font-family: var(--display); font-size: 1.05rem; letter-spacing: 0.12em; color: #fff; }
  .catExpRule { padding: 14px 18px; background: var(--blue-dim); border-bottom: 1px solid var(--border); font-size: 0.92rem; font-weight: 600; color: var(--blue-dark); line-height: 1.55; font-style: italic; }
  .catExpGrid { display: grid; grid-template-columns: 1fr 1fr; }
  .catExpCol { padding: 16px 18px; }
  .catExpCol:first-child { border-right: 1px solid var(--border); }
  .catExpColLabel { font-family: var(--mono); font-size: 0.52rem; letter-spacing: 0.18em; text-transform: uppercase; color: var(--blue); margin-bottom: 10px; }
  .catExpColLabel--reject { color: var(--red); }
  .catExpItem { padding: 8px 0; border-bottom: 1px solid var(--border); font-size: 0.84rem; line-height: 1.55; color: var(--blue-dark); }
  .catExpItem:last-child { border-bottom: none; }
  .signalPatternsGrid { display: grid; gap: 12px; }
  .signalPatternCard { border: 1px solid var(--border); background: #fff; overflow: hidden; }
  .signalPatternHeader { padding: 10px 16px; background: var(--blue-dark); }
  .signalPatternName { font-family: var(--display); font-size: 0.95rem; letter-spacing: 0.08em; color: #fff; }
  .signalPatternBody { padding: 14px 16px; display: grid; gap: 10px; }
  .signalPatternDesc { font-size: 0.86rem; line-height: 1.65; color: var(--muted); }
  .signalPatternPhrases { display: grid; gap: 5px; }
  .signalPatternPhrase { font-family: var(--mono); font-size: 0.68rem; color: var(--blue); padding: 5px 10px; background: var(--blue-dim); border: 1px solid rgba(43,127,212,0.2); font-style: italic; }
  .signalPatternImpact { border-left: 3px solid var(--blue); padding: 9px 12px; background: var(--blue-dim); font-size: 0.84rem; font-weight: 600; color: var(--blue-dark); line-height: 1.6; }
  .signalPatternSectionLabel { font-family: var(--mono); font-size: 0.48rem; letter-spacing: 0.18em; color: #6b7280; text-transform: uppercase; margin-bottom: 5px; }
  .diagnosisGrid { display: grid; gap: 14px; }
  .diagnosisCard { border: 1px solid var(--border); background: #fff; overflow: hidden; }
  .diagnosisHeader { padding: 10px 16px; background: var(--blue-dark); display: flex; align-items: center; gap: 10px; }
  .diagnosisType { font-family: var(--display); font-size: 0.95rem; letter-spacing: 0.08em; color: #fff; }
  .diagnosisBody { padding: 14px 16px; display: grid; gap: 10px; }
  .diagnosisDefinition { font-size: 0.88rem; line-height: 1.65; color: var(--muted); }
  .diagnosisSignals { display: grid; gap: 6px; }
  .diagnosisSignal { font-family: var(--mono); font-size: 0.72rem; color: var(--blue); padding: 6px 10px; background: var(--blue-dim); border: 1px solid rgba(43,127,212,0.2); font-style: italic; }
  .diagnosisImplication { border-left: 3px solid var(--blue); padding: 10px 14px; background: var(--blue-dim); font-size: 0.85rem; font-weight: 600; color: var(--blue-dark); line-height: 1.6; }
  .diagnosisSectionLabel { font-family: var(--mono); font-size: 0.5rem; letter-spacing: 0.18em; color: #6b7280; text-transform: uppercase; margin-bottom: 6px; }
  .adAngleGrid { display: grid; gap: 12px; }
  .adAngleCard { border: 1px solid var(--border); background: #fff; padding: 14px; }
  .adAngleName { font-family: var(--display); font-size: 0.95rem; letter-spacing: 0.08em; color: var(--blue-dark); margin-bottom: 8px; }
  .adAngleWhat { font-size: 0.85rem; line-height: 1.65; color: var(--muted); margin-bottom: 10px; }
  .adAngleLead { border-left: 3px solid var(--blue); padding: 8px 12px; background: var(--blue-dim); font-size: 0.85rem; font-weight: 600; color: var(--blue-dark); line-height: 1.55; margin-bottom: 10px; }
  .adAngleSignals { display: flex; flex-wrap: wrap; gap: 6px; }
  .adAngleSignal { font-family: var(--mono); font-size: 0.6rem; padding: 4px 8px; border: 1px solid var(--border); color: #6b7280; background: var(--bg); font-style: italic; }
  .strategicSummary { display: grid; gap: 14px; }
  .strategicSummaryBlock { border: 1px solid var(--border); background: #fff; overflow: hidden; }
  .strategicSummaryLabel { font-family: var(--mono); font-size: 0.52rem; letter-spacing: 0.18em; color: var(--blue); padding: 8px 14px; background: var(--blue-dim); border-bottom: 1px solid var(--border); text-transform: uppercase; }
  .strategicSummaryItem { padding: 10px 14px; border-bottom: 1px solid var(--border); font-size: 0.86rem; line-height: 1.65; color: var(--blue-dark); }
  .strategicSummaryItem:last-child { border-bottom: none; }
  /* ── EXECUTIVE SUMMARY [ADDED 2026-03-31] ── */
  .execSummary { background: #f0f5fb; border-left: 4px solid var(--blue); margin: 16px 0 0; padding: 20px 28px; border-radius: 0 6px 6px 0; }
  .execSummaryHeader { font-family: var(--mono); font-size: 0.7rem; letter-spacing: 0.12em; color: var(--blue); font-weight: 700; margin-bottom: 14px; text-transform: uppercase; }
  .execSummaryBody p { margin: 0 0 9px; font-size: 0.93rem; color: #2a2f3a; line-height: 1.6; }
  .execSummaryBody p:last-child { margin-bottom: 0; }
  .execLabel { font-weight: 700; color: var(--blue-dark); font-size: 0.72rem; letter-spacing: 0.05em; text-transform: uppercase; display: inline-block; margin-right: 6px; font-family: var(--mono); }
  /* ── HOW CUSTOMERS THINK ABOUT THIS CATEGORY [ADDED 2026-03-31] ── */
  .categoryThinking { background: #fff; border: 1px solid var(--border); margin: 16px 0 0; overflow: hidden; }
  .categoryThinkingHeader { font-family: var(--mono); font-size: 0.7rem; letter-spacing: 0.14em; text-transform: uppercase; padding: 12px 20px; background: var(--blue-dark); color: #fff; font-weight: 700; }
  .categoryThinkingSetup { padding: 16px 20px 12px; font-size: 0.92rem; line-height: 1.7; color: var(--muted); border-bottom: 1px solid var(--border); }
  .categoryThinkingQuestions { display: grid; gap: 0; }
  .categoryThinkingQuestion { font-family: var(--mono); font-size: 0.75rem; color: var(--blue-dark); padding: 10px 20px; border-bottom: 1px solid var(--border); line-height: 1.5; }
  .categoryThinkingQuestion::before { content: '"'; color: var(--blue); }
  .categoryThinkingQuestion::after { content: '"'; color: var(--blue); }
  .categoryThinkingTranslation { padding: 14px 20px; font-size: 0.85rem; line-height: 1.7; color: var(--blue-dark); font-weight: 500; background: var(--blue-dim); border-top: 2px solid var(--blue); font-style: italic; }
  .hookBlock { border: 2px solid var(--blue-dark); background: var(--blue-dark); margin-top: 16px; overflow: hidden; }
  .hookInsight { padding: 20px 24px 16px; border-bottom: 1px solid rgba(255,255,255,0.12); }
  .hookInsightLabel { font-family: var(--mono); font-size: 0.52rem; letter-spacing: 0.22em; color: var(--blue-light); margin-bottom: 10px; }
  .hookInsightText { font-family: var(--display); font-size: clamp(1.3rem, 3vw, 1.9rem); letter-spacing: 0.04em; color: #fff; line-height: 1.2; }
  .hookGrid { display: grid; grid-template-columns: 1fr 1fr 1fr; border-top: 1px solid rgba(255,255,255,0.1); }
  .hookCell { padding: 16px 20px; border-right: 1px solid rgba(255,255,255,0.1); }
  .hookCell:last-child { border-right: none; }
  .hookCellLabel { font-family: var(--mono); font-size: 0.5rem; letter-spacing: 0.2em; color: var(--blue-light); margin-bottom: 8px; text-transform: uppercase; }
  .hookCellText { font-size: 0.86rem; line-height: 1.65; color: rgba(255,255,255,0.85); }
  .hookProof { padding: 14px 24px; border-top: 1px solid rgba(255,255,255,0.1); display: grid; gap: 6px; }
  .hookProofLabel { font-family: var(--mono); font-size: 0.5rem; letter-spacing: 0.2em; color: var(--blue-light); margin-bottom: 4px; text-transform: uppercase; }
  .hookProofQuote { font-family: var(--mono); font-size: 0.72rem; color: rgba(255,255,255,0.7); font-style: italic; padding: 5px 10px; border-left: 2px solid var(--blue-light); }
  .misalignBlock { border: 2px solid var(--red); background: #fff; margin-top: 16px; overflow: hidden; }
  .misalignHeader { background: var(--red); padding: 12px 18px; }
  .misalignHeaderText { font-family: var(--display); font-size: 1.1rem; letter-spacing: 0.12em; color: #fff; }
  .misalignGrid { display: grid; grid-template-columns: 1fr 1fr; border-bottom: 1px solid rgba(220,38,38,0.15); }
  .misalignCell { padding: 16px 18px; }
  .misalignCell:first-child { border-right: 1px solid rgba(220,38,38,0.15); }
  .misalignCellLabel { font-family: var(--mono); font-size: 0.5rem; letter-spacing: 0.2em; color: var(--red); margin-bottom: 8px; text-transform: uppercase; }
  .misalignCellText { font-size: 0.9rem; line-height: 1.65; color: var(--blue-dark); font-weight: 600; }
  .misalignWhy { padding: 14px 18px; border-bottom: 1px solid rgba(220,38,38,0.15); background: var(--red-dim); }
  .misalignWhyLabel { font-family: var(--mono); font-size: 0.5rem; letter-spacing: 0.2em; color: var(--red); margin-bottom: 8px; text-transform: uppercase; }
  .misalignWhyText { font-size: 0.88rem; line-height: 1.7; color: var(--muted); }
  .misalignEvidence { padding: 12px 18px; display: grid; gap: 6px; border-bottom: 1px solid rgba(220,38,38,0.15); }
  .misalignEvidenceLabel { font-family: var(--mono); font-size: 0.5rem; letter-spacing: 0.2em; color: var(--red); margin-bottom: 4px; text-transform: uppercase; }
  .misalignQuote { font-family: var(--mono); font-size: 0.72rem; color: var(--red); font-style: italic; padding: 5px 10px; border-left: 2px solid var(--red); background: var(--red-dim); }
  .misalignClosing { padding: 14px 18px; background: var(--red); }
  .misalignClosingText { font-size: 0.92rem; font-weight: 700; color: #fff; line-height: 1.55; }
  @media (max-width: 960px) {
    .breakdownDataGrid { grid-template-columns: repeat(2, 1fr); }
    .breakdownThreeCol { grid-template-columns: 1fr; }
    .hookGrid { grid-template-columns: 1fr; }
    .misalignGrid { grid-template-columns: 1fr; }
    .catExpGrid { grid-template-columns: 1fr; }
    .brandPosGrid { grid-template-columns: 1fr; }
  }
  @media (max-width: 860px) {
    .grid2, .reportHead, .checkboxGrid { grid-template-columns: 1fr; }
    .categoryBox { text-align: left; }
    .topbar { padding: 16px 18px; }
    .wrap { padding: 34px 16px 60px; }
    .catExpGrid { grid-template-columns: 1fr; }
    .brandPosGrid { grid-template-columns: 1fr; }
  }

  /* ── DARK NAVY INPUT PANEL (form-only; report preview untouched) ── */
  .panel { background: #111d33; border-color: #1e2f55; box-shadow: 0 10px 40px -12px rgba(8,15,28,0.55); }
  .panelBar { background: #0d1526; border-bottom-color: #1e2f55; }
  .panelBarText { color: #60a5fa; }
  .dot { background: #2563eb; }
  .panel .label { color: #aac4ea; }
  .panel .input { background: #0d1526; border-color: #1e2f55; color: #f0f4ff; }
  .panel .input::placeholder { color: #8ea8d0; }
  .panel .input:focus { border-color: #2563eb; box-shadow: 0 0 0 3px rgba(37,99,235,0.18); }
  .panel select.input option { background: #0d1526; color: #f0f4ff; }
  .panel .checkboxItem { background: #0d1526; border-color: #1e2f55; color: #cdddf5; }
  .panel .checkboxItem:hover { border-color: #2563eb; background: rgba(37,99,235,0.12); }
  .panel .checkboxItem.disabled { background: #0a1120; }
  .panel .checkboxItem.disabled:hover { border-color: #1e2f55; background: #0a1120; }
  .panel .checkboxItem input[type=checkbox] { accent-color: #2563eb; }
  .panel .checkboxHint { color: #8ea8d0; }
  .formAdv { background: #0d1526; border-color: #1e2f55; }
  .formAdvHead { background: #0a1120; }
  .formAdvHead:hover { background: rgba(37,99,235,0.12); }
  .formAdvTitle { color: #60a5fa; }
  .formAdvCount { background: #2563eb; }
  .formAdvChevron { color: #7a9cc8; }
  .formAdvBody { background: #111d33; border-top-color: #1e2f55; }
  .runBtn { background: #2563eb; }
  .runBtn:hover { background: #3b82f6; }
  .runBtnAlt { background: transparent; color: #60a5fa; border-color: #2563eb; }
  .runBtnAlt:hover { background: #16233d; }
  .chainDone { background: #111d33; border-color: #1e2f55; border-left-color: #2563eb; }
  .chainDoneKicker { color: #60a5fa; }
  .chainDoneBody, .chainDoneNext { color: #9fb3d1; }
  .chainDoneCtxVal { background: #0b1524; border-color: #1e2f55; color: #cbd9ea; }
  .recentCtx { border-top-color: #1e2f55; }
  .recentCtxRow { border-bottom-color: #16233d; }
  .recentCtxDate { color: #9fb3d1; }
  .recentCtxId { color: #7f93ad; }
  .recentCtxRow:hover .recentCtxId { color: #60a5fa; }
  .error { color: #f87171; }
`;

// ─── CONSTANTS ────────────────────────────────────────────────────────────────

const initialOpen = {
  hook: true, misalignment: true, brandPositioning: true, categoryExpectations: true,
  strategicSummary: true, adAngles: true, breakdown: true,
  coverage: true, customerLanguage: true, signalPatterns: true,
  diagnosis: true, summary: true, clusters: true, evidence: true, signals: false,
};

const loadingSteps = [
  "Initializing brand profile",
  "Fetching homepage and extracting signals",
  "Checking public evidence",
  "Gathering reviews and comments",
  "Interpreting intent patterns",
  "Generating semantic intent clusters",
];

// A chain-only run returns from the worker before it sets step 4, so it can
// only ever reach index 3 — the last two steps above never happen and the bar
// would snap from 67% to 100% at the end. These are the steps that DO run: the
// same first four, then the freeze/seed the worker actually performs in place
// of the gpt-4o call.
const chainOnlyLoadingSteps = [
  ...loadingSteps.slice(0, 4),
  "Freezing corpus and seeding shape templates",
];

// ─── SHARED UI COMPONENT ──────────────────────────────────────────────────────

function Section({ title, open, onToggle, children }) {
  return (
    <div className="section">
      <div className="sectionHead" onClick={onToggle}>
        <div className="sectionTitle">{title}</div>
        <div className="sectionToggle">{open ? "COLLAPSE" : "EXPAND"}</div>
      </div>
      {open && <div className="sectionBody">{children}</div>}
    </div>
  );
}


// ─── MAIN APP COMPONENT ───────────────────────────────────────────────────────

export default function App() {
  // ── Unified Ntentify auth (server-side via /api/auth) ──
  const [unlocked, setUnlocked] = useState(false);
  const [pwInput, setPwInput] = useState("");
  const [codeInput, setCodeInput] = useState("");
  const [pwError, setPwError] = useState(false);
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch('https://admin.ntentify.com/api/auth', {
          method: 'GET',
          credentials: 'include'
        });
        if (res.ok) {
          const data = await res.json();
          if (data.authenticated) setUnlocked(true);
        }
      } catch (e) {}
    })();
  }, []);
  const [brandName, setBrandName] = useState("");
  const [website, setWebsite] = useState("");
  const [brandType, setBrandType] = useState("dtc");
  const [sources, setSources] = useState({
    amazon: true,
    walmart: true,
    trustpilot: true,
    reddit: true,
    youtube: true,
    paa: true,
    marketContext: true,
    appstore: false,
  });
  const [loading, setLoading] = useState(false);
  // Which of the two build buttons is in flight: "full" | "chain" | null. Both
  // share `loading`, so without this the wrong button reads "RUNNING…".
  const [runMode, setRunMode] = useState(null);
  const [progressIndex, setProgressIndex] = useState(0);
  const [error, setError] = useState("");
  const [report, setReport] = useState(null);
  // A completed chain-only job: { contextId, brand }. Held separately from
  // `report` because there is no report — the run's whole output is a frozen
  // context id the operator carries to the Synopsis Review console.
  const [chainOnlyDone, setChainOnlyDone] = useState(null);
  // The five newest frozen contexts for whatever brand is typed above. This is
  // the durable half of the pair: chainOnlyDone is the just-finished run and
  // dies with the tab, these are read back from report_context and survive a
  // refresh, a crash, or the operator going home. Never holds context_json.
  const [recentContexts, setRecentContexts] = useState([]);
  // Bumped when a chain-only run lands so the list below picks up the context
  // it just produced without waiting for the brand field to change.
  const [recentContextsNonce, setRecentContextsNonce] = useState(0);
  const [signals, setSignals] = useState(null);
  const [evidence, setEvidence] = useState(null);
  const [openMap, setOpenMap] = useState(initialOpen);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [reportType, setReportType] = useState("full");
  const [includeGeoSignal, setIncludeGeoSignal] = useState(false);
  const [geoKeywords, setGeoKeywords] = useState("");
  const [knownActivity, setKnownActivity] = useState({
    ctv: false, longForm: false, youtube: false, podcasts: false, socialAds: false,
  });
  const [redditKeywords, setRedditKeywords] = useState("");
  // Multi-seed state (Session 40). Three discrete input fields enforce the
  // multi-seed discipline standing rule in the UI — operator sees three
  // slots and is nudged to fill three. All three optional at the form level;
  // worker drops empties, dedupes, caps at 3 defensively. If only one is
  // filled, run still works as a one-seed run.
  const [marketContextSeed1, setMarketContextSeed1] = useState("");
  const [marketContextSeed2, setMarketContextSeed2] = useState("");
  const [marketContextSeed3, setMarketContextSeed3] = useState("");
  const [whiteLabelConfigId, setWhiteLabelConfigId] = useState("");
  const [whiteLabelConfigs, setWhiteLabelConfigs] = useState([]);
  const [formAdv, setFormAdv] = useState({ sourcing: false, runopts: false });
  const [rosterBrands, setRosterBrands] = useState([]);
  const [rosterUrls, setRosterUrls] = useState({});
  const [brandOpen, setBrandOpen] = useState(false);
  const [brandActive, setBrandActive] = useState(-1);

  // One-time cleanup of the old un-keyed seeds key.
  useEffect(() => {
    try { localStorage.removeItem('ntentify_market_seeds'); } catch (_) {}
  }, []);

  // When brandName changes, look up the brand-keyed seeds map and prefill if found.
  // Does not clear operator-typed seeds when no match exists.
  useEffect(() => {
    if (!brandName.trim()) return;
    const lowerBrand = brandName.trim().toLowerCase();
    try {
      const map = JSON.parse(localStorage.getItem('ntentify_market_seeds_by_brand') || '{}');
      const key = Object.keys(map).find(k => k.toLowerCase() === lowerBrand);
      if (key && Array.isArray(map[key]) && map[key].length) {
        if (map[key][0]) setMarketContextSeed1(map[key][0]);
        if (map[key][1]) setMarketContextSeed2(map[key][1]);
        if (map[key][2]) setMarketContextSeed3(map[key][2]);
        setGeoKeywords(map[key].filter(Boolean).join(', '));
      }
    } catch (_) {}
    const urlKey = Object.keys(rosterUrls).find(k => k.toLowerCase() === lowerBrand);
    if (urlKey && rosterUrls[urlKey]) setWebsite(rosterUrls[urlKey]);
  }, [brandName, rosterUrls]);

  // ── Load white label configs after auth ──
  useEffect(() => {
    if (!unlocked) return;
    (async () => {
      try {
        const r = await fetch(
          'https://rnwlqflhmqzqiagtdrmo.supabase.co/rest/v1/white_label_configs?order=agency_name.asc&select=id,agency_name,tagline',
          {
            headers: {
              'apikey': 'sb_publishable_y3nuaUa17Pbk66avBTNu-g_z46PFZkZ',
              'Authorization': 'Bearer sb_publishable_y3nuaUa17Pbk66avBTNu-g_z46PFZkZ',
            }
          }
        );
        if (r.ok) setWhiteLabelConfigs(await r.json());
      } catch(e) {}
    })();
  }, [unlocked]);

  // ── Marketplace mode auto-disables Amazon + Walmart, restores when flipped back to dtc ──
  useEffect(() => {
    if (brandType === "marketplace" || brandType === "service") {
      setSources(prev => ({ ...prev, amazon: false, walmart: false }));
    } else {
      setSources(prev => ({ ...prev, amazon: true, walmart: true }));
    }
  }, [brandType]);

  useEffect(() => {
    if (!unlocked) return;
    (async () => {
      try {
        const r = await fetch('/api/roster-brands', { credentials: 'include' });
        if (r.ok) { const d = await r.json(); setRosterBrands(d.brands || []); setRosterUrls(d.urls || {}); }
      } catch (e) { console.error('roster-brands fetch failed; brand field degrades to free-text', e); }
    })();
  }, [unlocked]);

  // Recent contexts for the typed brand. Debounced because brandName changes on
  // every keystroke and this is a database read; 400ms is past the end of a
  // word without being noticeable. The nonce in the deps is what refreshes the
  // list when a run completes.
  //
  // A failure here is deliberately silent in the UI: this block is a fallback
  // for a lost page, not a control, and an error banner on the run form for a
  // failed convenience read would train the operator to ignore the banner.
  useEffect(() => {
    if (!unlocked) return;
    const brand = brandName.trim();
    if (!brand) { setRecentContexts([]); return; }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const r = await fetch(`/api/recent-contexts?brand=${encodeURIComponent(brand)}`, { credentials: 'include' });
        if (!r.ok) return;
        const d = await r.json();
        if (!cancelled) setRecentContexts(Array.isArray(d.contexts) ? d.contexts : []);
      } catch (e) {
        console.error('recent-contexts fetch failed; the run button is unaffected', e);
      }
    }, 400);
    return () => { cancelled = true; clearTimeout(t); };
  }, [unlocked, brandName, recentContextsNonce]);

  const toggleSource = (key) => {
    if ((brandType === "marketplace" || brandType === "service") && (key === "amazon" || key === "walmart")) return;
    setSources(prev => ({ ...prev, [key]: !prev[key] }));
  };

  if (!unlocked) {
    const tryAuth = async () => {
      try {
        const res = await fetch('https://admin.ntentify.com/api/auth', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ password: pwInput, code: codeInput })
        });
        if (res.ok) {
          setUnlocked(true);
        } else {
          setPwError(true);
          setPwInput('');
          setCodeInput('');
        }
      } catch (e) {
        setPwError(true);
        setPwInput('');
        setCodeInput('');
      }
    };
    return (
      <div style={{minHeight:'100vh',background:'#080f1c',display:'flex',alignItems:'center',justifyContent:'center'}}>
        <div style={{background:'#111d33',border:'1px solid #1e2f55',padding:'48px 52px',textAlign:'center',width:380}}>
          <div style={{fontFamily:'Bebas Neue,sans-serif',fontSize:32,letterSpacing:'0.08em',color:'#f0f4ff',marginBottom:6}}>Ntent<span style={{color:'#3b82f6'}}>ify</span></div>
          <div style={{fontFamily:'DM Mono,monospace',fontSize:10,letterSpacing:'0.15em',color:'#4a6a99',textTransform:'uppercase',marginBottom:36}}>Signal Interpretation Engine</div>
          <input
            type="password"
            placeholder="Enter password"
            value={pwInput}
            onChange={e => { setPwInput(e.target.value); setPwError(false); }}
            onKeyDown={e => e.key === 'Enter' && tryAuth()}
            style={{width:'100%',background:'#080f1c',border:`1px solid ${pwError ? '#ef4444' : '#1e2f55'}`,color:'#f0f4ff',fontFamily:'DM Mono,monospace',fontSize:14,padding:'12px 16px',outline:'none',textAlign:'center',letterSpacing:'0.1em',marginBottom:12}}
          />
          <input
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            placeholder="6-digit code"
            value={codeInput}
            onChange={e => { setCodeInput(e.target.value.replace(/\D/g, '').slice(0, 6)); setPwError(false); }}
            onKeyDown={e => e.key === 'Enter' && tryAuth()}
            style={{width:'100%',background:'#080f1c',border:`1px solid ${pwError ? '#ef4444' : '#1e2f55'}`,color:'#f0f4ff',fontFamily:'DM Mono,monospace',fontSize:14,padding:'12px 16px',outline:'none',textAlign:'center',letterSpacing:'0.3em',marginBottom:12}}
          />
          <button
            onClick={tryAuth}
            style={{width:'100%',background:'#2563eb',color:'#fff',border:'none',fontFamily:'DM Mono,monospace',fontSize:11,letterSpacing:'0.12em',textTransform:'uppercase',padding:12,cursor:'pointer'}}
          >Enter</button>
          {pwError && <div style={{fontFamily:'DM Mono,monospace',fontSize:11,color:'#ef4444',marginTop:10}}>Incorrect password or code</div>}
        </div>
      </div>
    );
  }

  // The in-flight run has no result payload to read yet, so the steps shown
  // while it runs are the one thing that must key off runMode. The COMPLETION
  // state below keys off the result instead.
  const activeSteps = runMode === "chain" ? chainOnlyLoadingSteps : loadingSteps;
  const progressPct = ((progressIndex + 1) / activeSteps.length) * 100;

  // chainOnly must be passed explicitly by each button — never wired as
  // onClick={runAudit}, which would hand React's MouseEvent in as the flag and
  // make every click a chain-only run.
  const runAudit = async (chainOnly = false) => {
    setError(""); setReport(null); setSignals(null); setEvidence(null); setChainOnlyDone(null);
    if (!brandName.trim() || !website.trim()) { setError("Enter a brand name and website."); return; }
    setLoading(true); setRunMode(chainOnly ? "chain" : "full"); setProgressIndex(0);
    try {
      const dispatchRes = await fetch("/api/audit", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          brandName: brandName.trim(),
          website: website.trim(),
          brandType,
          sources,
          redditKeywords: redditKeywords.trim() || null,
          marketContextCategorySeeds: [marketContextSeed1, marketContextSeed2, marketContextSeed3]
            .map(s => s.trim())
            .filter(Boolean),
          knownActivity,
          whiteLabelConfigId: whiteLabelConfigId || null,
          includeGeoSignal,
          geoKeywords: geoKeywords.trim()
            ? geoKeywords.split(",").map(k => k.trim()).filter(Boolean)
            : [brandName.trim()],
          chainOnly,
        }),
      });
      const dispatchData = await dispatchRes.json();
      if (!dispatchRes.ok) throw new Error(dispatchData?.error || "Failed to start analysis");
      const { jobId } = dispatchData;
      if (!jobId) throw new Error("No job ID returned");
      await new Promise((resolve, reject) => {
        const MAX_WAIT_MS = 30 * 60 * 1000;
        const POLL_INTERVAL = 3000;
        const startedAt = Date.now();
        const poll = async () => {
          try {
            if (Date.now() - startedAt > MAX_WAIT_MS) { reject(new Error("Analysis timed out")); return; }
            const statusRes = await fetch(`/api/job-status?jobId=${jobId}`);
            const statusData = await statusRes.json();
            if (!statusRes.ok) { reject(new Error(statusData?.error || "Status check failed")); return; }
            const { status, stepIndex, error, result } = statusData;
            if (typeof stepIndex === "number") setProgressIndex(stepIndex);
            if (status === "complete" && result) {
              // Branch on the payload, not on runMode: the job says what it
              // produced, and that survives a refresh or a reattached poll in a
              // way client state does not. A chain-only job completes with
              // { context_id, brand, chain_only } and no report — running the
              // setReport path on it would blank the page with undefined and
              // throw away the one value the operator needs next.
              if (!result.report) {
                setChainOnlyDone({ contextId: result.context_id, brand: result.brand || brandName.trim() });
                // Re-read report_context so the run that just finished appears
                // in Recent contexts immediately. The panel above is still the
                // primary hand-off; this is what is left after a refresh.
                setRecentContextsNonce(n => n + 1);
                setProgressIndex(chainOnlyLoadingSteps.length - 1);
                resolve(); return;
              }
              const reportWithGeo = result.geo_signal ? { ...result.report, geo_signal: result.geo_signal } : result.report;
              setReport(reportWithGeo); setSignals(result.signals); setEvidence(result.evidence);
              setOpenMap(initialOpen); setProgressIndex(loadingSteps.length - 1);
              resolve(); return;
            }
            if (status === "failed") { reject(new Error(error || "Analysis failed")); return; }
            setTimeout(poll, POLL_INTERVAL);
          } catch (err) { reject(err); }
        };
        setTimeout(poll, 1500);
      });
    } catch (err) { setError(err.message || "Something went wrong.");
    } finally { setLoading(false); setRunMode(null); }
  };

 const handleDownloadAllHTML = async () => {
  if (!report) return;
  try {
    setPdfLoading(true); setError("");
    const slug = (report.brand_name || "report").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

    if (!window.JSZip) {
      await new Promise((resolve, reject) => {
        const script = document.createElement("script");
        script.src = "https://cdnjs.cloudflare.com/ajax/libs/jszip/3.10.1/jszip.min.js";
        script.onload = resolve;
        script.onerror = reject;
        document.head.appendChild(script);
      });
    }

    const zip = new window.JSZip();
    zip.file("index.html", generatePortalHTML(report));
    zip.file("ad-angle-report.html", generateAdAngleReportHTML(report));
    zip.file("message-gap-report.html", generateMessageGapReportHTML(report, signals));
    zip.file("customer-signals-report.html", generateCustomerSignalsReportHTML(report));
    zip.file("strategy-report.html", generateReportHTML(report, signals, evidence, knownActivity));
    zip.file("executive-summary.html", generateExecutiveSummaryHTML(report, signals));

    const blob = await zip.generateAsync({ type: "blob" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${slug}-reports.zip`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  } catch (err) { setError(`Export failed: ${err.message || err}`);
  } finally { setPdfLoading(false); }
};
  const handleDownloadHTML = async () => {
    if (!report) return;
    try {
      setPdfLoading(true); setError("");
      const slug = (report.brand_name || "report").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
      if (reportType === "portal") {
        const html = generatePortalHTML(report);
        downloadHTML(html, `${slug}-portal.html`);
      } else if (reportType === "adangles") {
        const html = generateAdAngleReportHTML(report);
        downloadHTML(html, `${slug}-ad-angle-report.html`);
      } else if (reportType === "messagegap") {
        const html = generateMessageGapReportHTML(report, signals);
        downloadHTML(html, `${slug}-message-gap-report.html`);
      } else if (reportType === "customersignals") {
        const html = generateCustomerSignalsReportHTML(report);
        downloadHTML(html, `${slug}-customer-signals-report.html`);
      } else if (reportType === "execsummary") {
        const html = generateExecutiveSummaryHTML(report, signals);
        downloadHTML(html, `${slug}-executive-summary.html`);
      } else {
        const html = generateReportHTML(report, signals, evidence, knownActivity);
        downloadHTML(html, `${slug}-strategy-report.html`);
      }
    } catch (err) { setError(`Export failed: ${err.message || err}`);
    } finally { setPdfLoading(false); }
  };

  const handleDownloadPDF = async () => {
    if (!report) return;
    try {
      setPdfLoading(true); setError("");
      const slug = (report.brand_name || "report").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
      const html = reportType === "adangles"
        ? generateAdAngleReportHTML(report)
        : reportType === "messagegap"
          ? generateMessageGapReportHTML(report, signals)
          : reportType === "customersignals"
            ? generateCustomerSignalsReportHTML(report)
            : generateReportHTML(report, signals, evidence, knownActivity);
      const filename = reportType === "adangles"
        ? `${slug}-ad-angle-report.pdf`
        : reportType === "messagegap"
          ? `${slug}-message-gap-report.pdf`
          : reportType === "customersignals"
            ? `${slug}-customer-signals-report.pdf`
            : `${slug}-semantic-intent-cluster.pdf`;
      const response = await fetch(
        "https://semantic-intent-pdf-service-production.up.railway.app/generate-pdf",
        { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ html }) }
      );
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || err.error || "PDF generation failed");
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) { setError(`PDF failed: ${err.message || err}`);
    } finally { setPdfLoading(false); }
  };

  const toggle = (key) => setOpenMap((prev) => ({ ...prev, [key]: !prev[key] }));
  const toggleKnownActivity = (key) => setKnownActivity((prev) => ({ ...prev, [key]: !prev[key] }));
  const toggleFormAdv = (key) => setFormAdv((prev) => ({ ...prev, [key]: !prev[key] }));

  const lang = normaliseCustomerLanguage(report?.real_customer_language);
  const ss = report?.strategic_summary || {};
  const hook = report?.condensed_strategic_hook || {};
  const mis = report?.core_strategic_misalignment || {};
  const es = report?.executive_summary || {};
  const ct = report?.category_thinking || {};
  const cat = report?.category_expectations || {};
  const bp = report?.brand_positioning || {};
  const recommendedAngles = safeList(report?.recommended_ad_angles).map(normaliseAdAngle);
  const sourcingActive = [redditKeywords, marketContextSeed1, marketContextSeed2, marketContextSeed3].filter(v => v.trim()).length;

  const runOptsActive = Object.values(knownActivity).filter(Boolean).length + (whiteLabelConfigId ? 1 : 0) + (includeGeoSignal ? 1 : 0);
  const brandQuery = brandName.trim().toLowerCase();
  const brandFiltered = rosterBrands.filter(b => b.toLowerCase().includes(brandQuery)).slice(0, 50);

  return (
    <div className="app">
      <style>{css}</style>

      {/* ── TOPBAR ── */}
      <div className="topbar">
        <div className="brand">
          <div className="brandMark">SC</div>
          <div>
            <div className="brandTitle">SEMANTIC INTENT CLUSTER</div>
            <div className="brandSub">A NTENTIFY PRODUCT · SIGNAL INTERPRETATION ENGINE</div>
          </div>
        </div>
        <div className="topMeta">
          <div className="tag live">SEMANTIC INTELLIGENCE</div>
          <div className="tag">INTENT STRUCTURING</div>
          <div className="tag">DIRECT RESPONSE OUTPUT</div>
        </div>
      </div>

      <div className="wrap">

        {/* ── HERO ── */}
        <div className="hero">
          <div className="eyebrow">SEMANTIC INTENT CLUSTER / SIGNAL INTERPRETATION ENGINE</div>
          <h1 className="headline">THE <em>SIGNAL</em> IS THE <em>STRATEGY</em>.</h1>
          <div className="subhead">This engine analyzes real customer language, interprets intent patterns, and surfaces exactly why your marketing is failing — and what needs to change.</div>
        </div>

        {/* ── INPUT PANEL ── */}
        <div className="panel">
          <div className="panelBar"><div className="dot" /><div className="panelBarText">NEW SEMANTIC INTENT CLUSTER REPORT</div></div>
          <div className="panelBody">

            {/* CORE — always visible */}
            <div className="grid2">
              <div className="field">
                <div className="label">BRAND NAME</div>
                <div className="brandCombo">
                  <input
                    className="input"
                    value={brandName}
                    autoComplete="off"
                    placeholder="Brand Name"
                    onChange={(e) => {
                      setBrandName(e.target.value.replace(/\b\w/g, c => c.toUpperCase()));
                      setBrandOpen(true);
                      setBrandActive(-1);
                    }}
                    onFocus={() => { setBrandOpen(true); setBrandActive(-1); }}
                    onBlur={() => setBrandOpen(false)}
                    onKeyDown={(e) => {
                      if (!brandOpen) return;
                      const optCount = brandFiltered.length + (brandQuery ? 1 : 0);
                      if (e.key === 'ArrowDown') { e.preventDefault(); setBrandActive(a => Math.min(a + 1, optCount - 1)); }
                      else if (e.key === 'ArrowUp') { e.preventDefault(); setBrandActive(a => Math.max(a - 1, -1)); }
                      else if (e.key === 'Enter' && brandActive >= 0) {
                        e.preventDefault();
                        if (brandActive < brandFiltered.length) setBrandName(brandFiltered[brandActive]);
                        setBrandOpen(false);
                      }
                      else if (e.key === 'Escape') { setBrandOpen(false); }
                    }}
                  />
                  {brandOpen && (brandFiltered.length > 0 || brandQuery) && (
                    <div className="brandMatches">
                      {brandFiltered.map((b, i) => (
                        <div
                          key={b}
                          className={'brandMatch' + (i === brandActive ? ' active' : '')}
                          onMouseDown={(e) => { e.preventDefault(); setBrandName(b); setBrandOpen(false); }}
                        >{b}</div>
                      ))}
                      {brandQuery && (
                        <div
                          className={'brandMatch addnew' + (brandFiltered.length === brandActive ? ' active' : '')}
                          onMouseDown={(e) => { e.preventDefault(); setBrandOpen(false); }}
                        >Use &ldquo;{brandName.trim()}&rdquo; (new brand — not in roster)</div>
                      )}
                    </div>
                  )}
                </div>
              </div>
              <div className="field">
                <div className="label">WEBSITE</div>
                <input className="input" value={website} onChange={(e) => setWebsite(e.target.value)} placeholder="brand.com" />
              </div>
            </div>
            <div className="field" style={{ marginTop: 14 }}>
              <div className="label">BRAND TYPE</div>
              <select className="input" value={brandType} onChange={(e) => setBrandType(e.target.value)} style={{ cursor: "pointer" }}>
                <option value="dtc">DTC</option>
                <option value="marketplace">Marketplace</option>
                <option value="service">Service / Subscription</option>
              </select>
            </div>
            <div className="field" style={{ marginTop: 14 }}>
              <div className="label">SIGNAL SOURCES</div>
              <div className="checkboxGrid">
                <label className={`checkboxItem${(brandType === "marketplace" || brandType === "service") ? " disabled" : ""}`}>
                  <input type="checkbox" checked={sources.amazon} onChange={() => toggleSource("amazon")} disabled={brandType === "marketplace" || brandType === "service"} />
                  AMAZON{(brandType === "marketplace" || brandType === "service") && <span className="checkboxHint">(not used for this brand type)</span>}
                </label>
                <label className={`checkboxItem${(brandType === "marketplace" || brandType === "service") ? " disabled" : ""}`}>
                  <input type="checkbox" checked={sources.walmart} onChange={() => toggleSource("walmart")} disabled={brandType === "marketplace" || brandType === "service"} />
                  WALMART{(brandType === "marketplace" || brandType === "service") && <span className="checkboxHint">(not used for this brand type)</span>}
                </label>
                <label className="checkboxItem">
                  <input type="checkbox" checked={sources.trustpilot} onChange={() => toggleSource("trustpilot")} />
                  TRUSTPILOT
                </label>
                <label className="checkboxItem">
                  <input type="checkbox" checked={sources.reddit} onChange={() => toggleSource("reddit")} />
                  REDDIT
                </label>
                <label className="checkboxItem">
                  <input type="checkbox" checked={sources.youtube} onChange={() => toggleSource("youtube")} />
                  YOUTUBE
                </label>
                <label className="checkboxItem">
                  <input type="checkbox" checked={sources.paa} onChange={() => toggleSource("paa")} />
                  PAA (PRE-PURCHASE)
                </label>
                <label className="checkboxItem">
                  <input type="checkbox" checked={sources.marketContext} onChange={() => toggleSource("marketContext")} />
                  MARKET CONTEXT
                </label>
                <label className="checkboxItem">
                  <input type="checkbox" checked={sources.appstore} onChange={() => toggleSource("appstore")} />
                  APP STORE<span className="checkboxHint">(app-first brands)</span>
                </label>
              </div>
            </div>

            {/* ADVANCED SOURCING — collapsed by default */}
            <div className="formAdv">
              <div className="formAdvHead" onClick={() => toggleFormAdv("sourcing")}>
                <div className="formAdvTitle">ADVANCED SOURCING{sourcingActive > 0 && <span className="formAdvCount">{sourcingActive} set</span>}</div>
                <div className="formAdvChevron">{formAdv.sourcing ? "▾" : "▸"}</div>
              </div>
              {formAdv.sourcing && (
                <div className="formAdvBody">
                  <div className="field">
                    <div className="label">REDDIT KEYWORDS COLLECTED (RECORD ONLY)</div>
                    <input className="input" value={redditKeywords} onChange={(e) => setRedditKeywords(e.target.value)} placeholder="keywords you searched for the paste collection — saved to the run record, drives nothing" disabled={!sources.reddit} style={!sources.reddit ? { opacity: 0.45, cursor: "not-allowed" } : {}} />
                  </div>
                  <div className="field" style={{ marginTop: 14 }}>
                    <div className="label">MARKET CONTEXT CATEGORY SEEDS (PASTE TOP 3 FROM KEYWORD PICKER)</div>
                    <input className="input" value={marketContextSeed1} onChange={(e) => setMarketContextSeed1(e.target.value)} placeholder="Seed 1 — e.g. over the counter hearing aids" disabled={!sources.marketContext} style={!sources.marketContext ? { opacity: 0.45, cursor: "not-allowed", marginBottom: 8 } : { marginBottom: 8 }} />
                    <input className="input" value={marketContextSeed2} onChange={(e) => setMarketContextSeed2(e.target.value)} placeholder="Seed 2 — e.g. affordable hearing aids" disabled={!sources.marketContext} style={!sources.marketContext ? { opacity: 0.45, cursor: "not-allowed", marginBottom: 8 } : { marginBottom: 8 }} />
                    <input className="input" value={marketContextSeed3} onChange={(e) => setMarketContextSeed3(e.target.value)} placeholder="Seed 3 — e.g. comfortable hearing aids" disabled={!sources.marketContext} style={!sources.marketContext ? { opacity: 0.45, cursor: "not-allowed" } : {}} />
                  </div>
                </div>
              )}
            </div>

            {/* RUN OPTIONS — collapsed by default */}
            <div className="formAdv">
              <div className="formAdvHead" onClick={() => toggleFormAdv("runopts")}>
                <div className="formAdvTitle">RUN OPTIONS{runOptsActive > 0 && <span className="formAdvCount">{runOptsActive} set</span>}</div>
                <div className="formAdvChevron">{formAdv.runopts ? "▾" : "▸"}</div>
              </div>
              {formAdv.runopts && (
                <div className="formAdvBody">
                  <div className="field">
                    <div className="label">KNOWN ACTIVITY OVERRIDES (OPTIONAL)</div>
                    <div className="checkboxGrid">
                      <label className="checkboxItem"><input type="checkbox" checked={knownActivity.ctv} onChange={() => toggleKnownActivity("ctv")} />RUNNING CTV / TV</label>
                      <label className="checkboxItem"><input type="checkbox" checked={knownActivity.longForm} onChange={() => toggleKnownActivity("longForm")} />RUNNING LONG-FORM / DRTV</label>
                      <label className="checkboxItem"><input type="checkbox" checked={knownActivity.youtube} onChange={() => toggleKnownActivity("youtube")} />RUNNING YOUTUBE ADS</label>
                      <label className="checkboxItem"><input type="checkbox" checked={knownActivity.podcasts} onChange={() => toggleKnownActivity("podcasts")} />RUNNING PODCASTS</label>
                      <label className="checkboxItem"><input type="checkbox" checked={knownActivity.socialAds} onChange={() => toggleKnownActivity("socialAds")} />RUNNING SOCIAL ADS</label>
                    </div>
                  </div>
                  <div className="field" style={{ marginTop: 14 }}>
                    <div className="label">WHITE LABEL (OPTIONAL)</div>
                    <select className="input" value={whiteLabelConfigId} onChange={(e) => setWhiteLabelConfigId(e.target.value)} style={{ cursor: "pointer" }}>
                      <option value="">Ntentify branding (default)</option>
                      {whiteLabelConfigs.map(c => (
                        <option key={c.id} value={c.id}>{c.agency_name}{c.tagline ? ` — ${c.tagline}` : ""}</option>
                      ))}
                    </select>
                  </div>
                  <div className="field" style={{ marginTop: 14 }}>
                    <div className="label">GEOGRAPHIC SEARCH SIGNAL (OPTIONAL)</div>
                    <label className="checkboxItem" style={{ marginBottom: 8 }}>
                      <input type="checkbox" checked={includeGeoSignal} onChange={() => setIncludeGeoSignal(prev => !prev)} />
                      INCLUDE GEOGRAPHIC SEARCH SIGNAL
                    </label>
                    {includeGeoSignal && (
                      <input className="input" value={geoKeywords} onChange={(e) => setGeoKeywords(e.target.value)} placeholder={`${brandName || "brand name"} (edit to add category keywords)`} />
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* GENERATE CLUSTERS — runAudit(false) — was here. Removed from the UI
                only: the monolith path is still live end to end. runAudit still
                takes chainOnly and still has its false branch, api/audit.js still
                honours chainOnly:false (audit.js:201), and intentcluster-worker
                still runs the gpt-4o path when the flag is absent. Reachable as a
                fallback by POSTing /api/audit directly with chainOnly:false or
                omitted — just not exposed to a click. */}
            {/* Chain-only build: same corpus, frozen and seeded for the synopsis
                chain to write from. Produces no report here — the next step is
                the Synopsis Review console, not this page. */}
            <button className="runBtn runBtnAlt" disabled={loading} onClick={() => runAudit(true)}>{loading && runMode === "chain" ? "BUILDING CORPUS..." : "BUILD SYNOPSIS CORPUS"}</button>
            {error ? <div className="error">{error}</div> : null}

            {/* Recent contexts — the fallback that survives a refresh. The
                completion panel below is still the primary hand-off for the run
                you just did; this is how you reach one whose tab is gone. Ids
                are shown short because they are for recognition, not copying —
                the link carries the whole thing. */}
            {recentContexts.length > 0 && (
              <div className="recentCtx">
                <div className="recentCtxLabel">RECENT CONTEXTS</div>
                {recentContexts.map((c) => (
                  <a
                    key={c.id}
                    className="recentCtxRow"
                    href={`/synopsis-review.html?context=${encodeURIComponent(c.id)}`}
                  >
                    <span className="recentCtxDate">
                      {c.assembled_at ? new Date(c.assembled_at).toLocaleString() : "—"}
                    </span>
                    <span className="recentCtxId">{c.scratch === 'true' ? 'SCRATCH ' : ''}{String(c.id).slice(0, 8)}</span>
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* ── LOADING STATE ── */}
        {loading && (
          <div className="loadingBox">
            <div className="loadingHeader">
              <div className="loadingKicker">SEMANTIC INTENT ENGINE / ANALYSIS RUNNING</div>
              <div className="loadingTitle">BUILDING <span>{runMode === "chain" ? "CORPUS" : "CLUSTERS"}</span></div>
            </div>
            <div className="loadingSteps">
              {activeSteps.map((step, index) => {
                const state = index < progressIndex ? "ls-done" : index === progressIndex ? "ls-active" : "ls-idle";
                return (
                  <div className={`loadingStep ${state}`} key={step}>
                    <div className="stepIndicator">
                      {index < progressIndex && <div className="stepDotDone" />}
                      {index === progressIndex && <div className="stepDotActive"><div className="stepPulseRing" /><div className="stepPulseRing stepPulseRing2" /><div className="stepDotCore" /></div>}
                      {index > progressIndex && <div className="stepDotIdle" />}
                    </div>
                    <div className="stepNum">0{index + 1}</div>
                    <div className="stepLabel">{step}</div>
                    <div className="stepCheck">{index < progressIndex ? "✓" : ""}</div>
                  </div>
                );
              })}
            </div>
            <div className="loadingBar">
              <div className="loadingBarLabel">PROGRESS</div>
              <div className="loadingBarTrack"><div className="loadingBarFill" style={{ width: `${progressPct}%` }} /></div>
              <div className="loadingBarPct">{Math.round(progressPct)}%</div>
            </div>
          </div>
        )}

        {/* ── CHAIN-ONLY COMPLETION ──
            A chain-only run produces no report on this page, so without this
            block the form simply returns to empty and reads as a silent
            failure — the expensive misreading, since the recoveries it invites
            are re-running the build, or dropping to the monolith path (POST
            /api/audit with chainOnly:false, which is still live — it just has
            no button any more) and paying for the full gpt-4o run. Neither is
            needed: the context id below is what the run was for. */}
        {chainOnlyDone && (
          <div className="chainDone">
            <div className="chainDoneKicker">CHAIN-ONLY BUILD / COMPLETE</div>
            <h2 className="chainDoneTitle">{chainOnlyDone.brand}</h2>
            <div className="chainDoneBody">
              Corpus frozen and shape templates seeded. No report was generated on this
              page — the pages are written by the synopsis chain, from the synopsis.
            </div>
            <div className="chainDoneCtx">
              <span className="chainDoneCtxLabel">CONTEXT ID</span>
              <code className="chainDoneCtxVal">{chainOnlyDone.contextId}</code>
            </div>
            <div className="chainDoneNext">
              Next step: synthesize this context in the Synopsis Review console, approve the
              synopsis, then run the chain.
            </div>
            <a className="runBtn chainDoneBtn" href="/synopsis-review.html">OPEN SYNOPSIS REVIEW</a>
          </div>
        )}

        {/* ── REPORT ── */}
        {report && (
          <div className="report">

            {/* ── REPORT HEADER ── */}
            <div className="reportHead">
              <div>
                <div className="reportKicker">SEMANTIC INTENT CLUSTER / STRATEGY REPORT</div>
                <h2 className="reportTitle">{report.brand_name || brandName}</h2>
                <div className="reportMeta">{report.website} / {report.generated_at ? new Date(report.generated_at).toLocaleString() : "Generated now"}</div>
              </div>
              <div className="categoryBox">
                <div className="categoryLabel">CATEGORY</div>
                <div className="categoryValue">{report.category || "Consumer Brand"}</div>
              </div>
            </div>
            <div className="summaryStrip">{report.signals_summary || "Signals summarized from website, public evidence, and category context."}</div>

            {/* ── ACTIONS ROW — report type toggle + download buttons ── */}
            <div className="actionsRow">
              <div className="reportTypeToggle">
                <button
                  className={`reportTypeBtn${reportType === "full" ? " active" : ""}`}
                  onClick={() => setReportType("full")}
                >FULL REPORT</button>
                <button
                  className={`reportTypeBtn${reportType === "adangles" ? " active" : ""}`}
                  onClick={() => setReportType("adangles")}
                >AD ANGLE REPORT</button>
                <button
                  className={`reportTypeBtn${reportType === "messagegap" ? " active" : ""}`}
                  onClick={() => setReportType("messagegap")}
                >MESSAGE GAP REPORT</button>
                <button
                  className={`reportTypeBtn${reportType === "customersignals" ? " active" : ""}`}
                  onClick={() => setReportType("customersignals")}
                >CUSTOMER SIGNALS</button>
                <button
                  className={`reportTypeBtn${reportType === "portal" ? " active" : ""}`}
                  onClick={() => setReportType("portal")}
                >PORTAL</button>
                <button
                  className={`reportTypeBtn${reportType === "execsummary" ? " active" : ""}`}
                  onClick={() => setReportType("execsummary")}
                >EXEC SUMMARY</button>
              </div>
              <button className="actionBtn" onClick={handleDownloadHTML} disabled={pdfLoading}>
                DOWNLOAD HTML
              </button>
              <button className="actionBtn" onClick={handleDownloadAllHTML} disabled={pdfLoading}>
                DOWNLOAD ALL REPORTS
              </button>
              <button className="actionBtn" onClick={handleDownloadPDF} disabled={pdfLoading}>
                {pdfLoading ? <><span className="spinner" />BUILDING PDF...</> : "DOWNLOAD PDF"}
              </button>
            </div>

            {/* ── EXECUTIVE SUMMARY ── */}
            {(es.category_truth || es.the_gap) && (
              <div className="execSummary">
                <div className="execSummaryHeader">EXECUTIVE SUMMARY</div>
                <div className="execSummaryBody">
                  {es.category_truth && <p><span className="execLabel">Category Truth</span>{es.category_truth}</p>}
                  {es.customer_expectation && <p><span className="execLabel">Customer Expectation</span>{es.customer_expectation}</p>}
                  {es.brand_positioning && <p><span className="execLabel">Brand Positioning</span>{es.brand_positioning}</p>}
                  {es.the_gap && <p><span className="execLabel">The Gap</span>{es.the_gap}</p>}
                  {es.the_opportunity && <p><span className="execLabel">The Opportunity</span>{es.the_opportunity}</p>}
                </div>
              </div>
            )}

            {/* ── HOW CUSTOMERS THINK ABOUT THIS CATEGORY ── */}
            {(ct.category_setup || safeList(ct.customer_questions).length > 0) && (
              <div className="categoryThinking">
                <div className="categoryThinkingHeader">HOW CUSTOMERS THINK ABOUT THIS CATEGORY</div>
                {ct.category_setup && (
                  <div className="categoryThinkingSetup">{ct.category_setup}</div>
                )}
                {safeList(ct.customer_questions).length > 0 && (
                  <div className="categoryThinkingQuestions">
                    {safeList(ct.customer_questions).map((q, i) => (
                      <div className="categoryThinkingQuestion" key={i}>{q}</div>
                    ))}
                  </div>
                )}
                {ct.intent_translation && (
                  <div className="categoryThinkingTranslation">{ct.intent_translation}</div>
                )}
              </div>
            )}

            {/* ── STRATEGIC HOOK ── */}
            {hook.primary_insight && (
              <div className="hookBlock">
                <div className="hookInsight">
                  <div className="hookInsightLabel">PRIMARY STRATEGIC INSIGHT</div>
                  <div className="hookInsightText">{hook.primary_insight}</div>
                </div>
                {(hook.what_customers_want || hook.what_marketing_delivers || hook.the_gap) && (
                  <div className="hookGrid">
                    {hook.what_customers_want && <div className="hookCell"><div className="hookCellLabel">WHAT CUSTOMERS WANT</div><div className="hookCellText">{hook.what_customers_want}</div></div>}
                    {hook.what_marketing_delivers && <div className="hookCell"><div className="hookCellLabel">WHAT MARKETING DELIVERS</div><div className="hookCellText">{hook.what_marketing_delivers}</div></div>}
                    {hook.the_gap && <div className="hookCell"><div className="hookCellLabel">THE GAP</div><div className="hookCellText">{hook.the_gap}</div></div>}
                  </div>
                )}
                {safeList(hook.proof_phrases).length > 0 && (
                  <div className="hookProof">
                    <div className="hookProofLabel">PROOF FROM CUSTOMER SIGNALS</div>
                    {safeList(hook.proof_phrases).map((q, i) => <div className="hookProofQuote" key={i}>"{normalizeText(q)}"</div>)}
                  </div>
                )}
              </div>
            )}

            {/* ── STRATEGIC VERDICT ── */}
            {report.strategic_verdict && (
              <div style={{ background: "var(--red)", padding: "18px 24px", marginTop: 4, fontFamily: "var(--display)", fontSize: "clamp(1rem, 2.5vw, 1.3rem)", letterSpacing: "0.06em", color: "#fff", lineHeight: 1.3 }}>
                {report.strategic_verdict}
              </div>
            )}

            {/* ── CORE STRATEGIC MISALIGNMENT ── */}
            {mis.what_customers_are_trying_to_fix && (
              <div className="misalignBlock">
                <div className="misalignHeader"><div className="misalignHeaderText">{mis.title || "CUSTOMER INSIGHT & OPPORTUNITY"}</div></div>
                <div className="misalignGrid">
                  <div className="misalignCell"><div className="misalignCellLabel">WHAT CUSTOMERS ARE TRYING TO FIX</div><div className="misalignCellText">{mis.what_customers_are_trying_to_fix}</div></div>
                  <div className="misalignCell"><div className="misalignCellLabel">WHAT MARKETING IS SELLING</div><div className="misalignCellText">{mis.what_marketing_is_selling}</div></div>
                </div>
                {(mis.why_this_matters || mis.why_this_creates_failure) && (
                  <div className="misalignWhy">
                    <div className="misalignWhyLabel">WHERE THIS LEAVES PERFORMANCE ON THE TABLE</div>
                    <div className="misalignWhyText">{mis.why_this_matters || mis.why_this_creates_failure}</div>
                  </div>
                )}
                {safeList(mis.customer_evidence).length > 0 && (
                  <div className="misalignEvidence">
                    <div className="misalignEvidenceLabel">CUSTOMER EVIDENCE</div>
                    {safeList(mis.customer_evidence).map((q, i) => <div className="misalignQuote" key={i}>"{normalizeText(q)}"</div>)}
                  </div>
                )}
                {mis.closing_statement && <div className="misalignClosing"><div className="misalignClosingText">{mis.closing_statement}</div></div>}
              </div>
            )}

            {/* ── BRAND POSITIONING ── */}
            {(safeList(bp.positioning_summary).length > 0 || safeList(bp.direct_site_language).length > 0) && (
              <Section title="WHAT YOUR BRAND IS SAYING" open={openMap.brandPositioning} onToggle={() => toggle("brandPositioning")}>
                <div className="brandPosBlock">
                  <div className="brandPosHeader"><div className="brandPosHeaderText">YOUR POSITIONING · FROM YOUR WEBSITE</div></div>
                  <div className="brandPosGrid">
                    <div className="brandPosCol">
                      <div className="brandPosColLabel">Your Positioning</div>
                      {safeList(bp.positioning_summary).map((item, i) => <div className="brandPosItem" key={i}>{item}</div>)}
                    </div>
                    <div className="brandPosCol">
                      <div className="brandPosColLabel">Direct Site Language</div>
                      {safeList(bp.direct_site_language).map((q, i) => <div className="brandPosSiteQuote" key={i}>"{q}"</div>)}
                    </div>
                  </div>
                  {bp.value_presentation && <div className="brandPosValue"><div className="brandPosValueLabel">How You Present Value</div>{bp.value_presentation}</div>}
                  {safeList(bp.customer_interpretation).length > 0 && (
                    <div className="brandPosInterpret">
                      <div className="brandPosInterpretLabel">How Customers Interpret This</div>
                      <div className="brandPosInterpretTags">{safeList(bp.customer_interpretation).map((tag, i) => <div className="brandPosInterpretTag" key={i}>"{normalizeText(tag)}"</div>)}</div>
                    </div>
                  )}
                </div>
              </Section>
            )}

            {/* ── CATEGORY EXPECTATIONS ── */}
            {(cat.category_rule || safeList(cat.what_customers_expect).length > 0 || safeList(cat.what_customers_reject).length > 0) && (
              <Section title="CATEGORY EXPECTATIONS" open={openMap.categoryExpectations} onToggle={() => toggle("categoryExpectations")}>
                <div className="catExpBlock">
                  <div className="catExpHeader"><div className="catExpHeaderText">WHAT CUSTOMERS EXPECT FROM THIS CATEGORY</div></div>
                  {cat.category_rule && <div className="catExpRule">{cat.category_rule}</div>}
                  <div className="catExpGrid">
                    <div className="catExpCol">
                      <div className="catExpColLabel">What Customers Expect</div>
                      {safeList(cat.what_customers_expect).map((item, i) => <div className="catExpItem" key={i}>{item}</div>)}
                    </div>
                    <div className="catExpCol">
                      <div className="catExpColLabel catExpColLabel--reject">What Customers Reject</div>
                      {safeList(cat.what_customers_reject).map((item, i) => <div className="catExpItem" key={i}>{item}</div>)}
                    </div>
                  </div>
                </div>
              </Section>
            )}

            {/* ── STRATEGIC SUMMARY ── */}
            {(safeList(ss.top_reasons_marketing_is_failing).length > 0 || safeList(ss.top_messaging_shifts_needed).length > 0 || safeList(ss.highest_impact_ad_angles).length > 0) && (
              <Section title="STRATEGIC SUMMARY" open={openMap.strategicSummary} onToggle={() => toggle("strategicSummary")}>
                <div className="strategicSummary">
                  {safeList(ss.top_reasons_marketing_is_failing).length > 0 && (
                    <div className="strategicSummaryBlock">
                      <div className="strategicSummaryLabel">TOP REASONS MARKETING IS FAILING</div>
                      {safeList(ss.top_reasons_marketing_is_failing).map((r, idx) => <div className="strategicSummaryItem" key={idx}>{r}</div>)}
                    </div>
                  )}
                  {safeList(ss.top_messaging_shifts_needed).length > 0 && (
                    <div className="strategicSummaryBlock">
                      <div className="strategicSummaryLabel">TOP MESSAGING SHIFTS NEEDED</div>
                      {safeList(ss.top_messaging_shifts_needed).map((r, idx) => <div className="strategicSummaryItem" key={idx}>{r}</div>)}
                    </div>
                  )}
                  {safeList(ss.highest_impact_ad_angles).length > 0 && (
                    <div className="strategicSummaryBlock">
                      <div className="strategicSummaryLabel">HIGHEST IMPACT AD ANGLES</div>
                      {safeList(ss.highest_impact_ad_angles).map((r, idx) => (
                        <div className="strategicSummaryItem" key={idx}>{typeof r === "string" ? r : (r?.angle || r?.hook || r?.angle_name || "")}</div>
                      ))}
                    </div>
                  )}
                </div>
              </Section>
            )}

            {/* ── AD ANGLES ── */}
            {recommendedAngles.length > 0 && (
              <div className="adAnglesBlock">
                <div className="adAnglesHeader">
                  <div className="adAnglesHeaderText">WHAT YOU SHOULD RUN NEXT</div>
                  <div className="adAnglesSubtext">RECOMMENDED AD ANGLES</div>
                </div>
                <div className="adAnglesList">
                  {recommendedAngles.map((a, idx) => (
                    <div className="adAnglesItem" key={idx}>
                      <div className="adAnglesItemTop">
                        <div className="adAnglesNum">{idx + 1}</div>
                        <div className="adAnglesText">{a.hook}</div>
                      </div>
                      {(a.what_it_addresses || a.why) && <div className="adAnglesWhy">{a.what_it_addresses || a.why}</div>}
                      {(a.tied_to_stage || a.tied_to) && <div className="adAnglesTiedTo">{a.tied_to_stage || a.tied_to}</div>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ── SIGNAL BREAKDOWN ── */}
            {report.signal_breakdown && (
              <Section title="SIGNAL BREAKDOWN" open={openMap.breakdown} onToggle={() => toggle("breakdown")}>
                <div className="breakdownDataGrid">
                  <div className="breakdownStatBox"><div className="breakdownStatLabel">WEBSITE SIGNALS</div><div className="breakdownStatValue">{report.signal_breakdown.website_signals ?? "—"}</div></div>
                  <div className="breakdownStatBox"><div className="breakdownStatLabel">MARKET SIGNALS</div><div className="breakdownStatValue">{report.signal_breakdown.market_signals ?? "—"}</div></div>
                  <div className="breakdownStatBox"><div className="breakdownStatLabel">REVIEWS ANALYZED</div><div className="breakdownStatValue">{report.signal_breakdown.reviews_analyzed ?? "—"}</div></div>
                  <div className="breakdownStatBox"><div className="breakdownStatLabel">SOCIAL / VIDEO COMMENTS</div><div className="breakdownStatValue">{report.signal_breakdown.social_comments ?? "—"}</div></div>
                </div>
                <div className="breakdownThreeCol">
                  <div className="breakdownBlock"><div className="breakdownBlockLabel">TOP SIGNALS</div>{safeList(report.signal_breakdown.top_signals).map((item, idx) => <div className="breakdownItem" key={idx}>{normalizeText(item)}</div>)}</div>
                  <div className="breakdownBlock"><div className="breakdownBlockLabel">DOMINANT EMOTIONS</div>{safeList(report.signal_breakdown.dominant_emotions).map((item, idx) => <div className="breakdownItem" key={idx}>{item}</div>)}</div>
                  <div className="breakdownBlock"><div className="breakdownBlockLabel">KEY BUYING DRIVERS</div>{safeList(report.signal_breakdown.key_buying_drivers).map((item, idx) => <div className="breakdownItem" key={idx}>{item}</div>)}</div>
                </div>
              </Section>
            )}

            {/* ── SIGNAL COVERAGE ── */}
            {report.signal_coverage && (
              <Section title="SIGNAL COVERAGE" open={openMap.coverage} onToggle={() => toggle("coverage")}>
                <div className="coverageGrid">
                  {report.signal_coverage.reviews_analyzed > 0 && <div className="coverageBox"><div className="coverageNum">{report.signal_coverage.reviews_analyzed}</div><div className="coverageLabel">Reviews Analyzed</div></div>}
                  {report.signal_coverage.social_comments_analyzed > 0 && <div className="coverageBox"><div className="coverageNum">{report.signal_coverage.social_comments_analyzed}</div><div className="coverageLabel">Video Comments</div></div>}
                  {report.signal_coverage.google_queries_analyzed > 0 && <div className="coverageBox"><div className="coverageNum">{report.signal_coverage.google_queries_analyzed}</div><div className="coverageLabel">Google Queries</div></div>}
                  {report.signal_coverage.website_signals_extracted > 0 && <div className="coverageBox"><div className="coverageNum">{report.signal_coverage.website_signals_extracted}</div><div className="coverageLabel">Website Signals</div></div>}
                  {report.signal_coverage.market_signals_found > 0 && <div className="coverageBox"><div className="coverageNum">{report.signal_coverage.market_signals_found}</div><div className="coverageLabel">Market Signals</div></div>}
                  <div className="coverageBox" style={{borderColor:"var(--blue)",background:"var(--blue-dim)"}}><div className="coverageNum">{report.signal_coverage.total_signals_processed}</div><div className="coverageLabel">Total Signals</div></div>
                </div>
                {safeList(report.signal_coverage.sources).length > 0 && <div className="sourceTagList">{safeList(report.signal_coverage.sources).map((src, idx) => <div className="sourceTag" key={idx}>{src}</div>)}</div>}
              </Section>
            )}

            {/* ── REAL CUSTOMER LANGUAGE ── */}
            <Section title="REAL CUSTOMER LANGUAGE" open={openMap.customerLanguage} onToggle={() => toggle("customerLanguage")}>
              <div className="languageSection">
                {Object.entries(lang).filter(([, quotes]) => quotes.length > 0).length > 0
                  ? Object.entries(lang).filter(([, quotes]) => quotes.length > 0).map(([theme, quotes]) => (
                    <div className="languageTheme" key={theme}>
                      <div className="languageThemeLabel">{formatThemeLabel(theme)}</div>
                      <div className="languageGrid">{quotes.map((quote, idx) => <div className="languageQuote" key={idx}>"{normalizeText(quote)}"</div>)}</div>
                    </div>
                  ))
                  : <div className="bullet" style={{marginTop:0}}>Insufficient real customer quotes were found in this run.</div>
                }
              </div>
            </Section>

            {/* ── SIGNAL PATTERNS ── */}
            {safeList(report.signal_patterns).length > 0 && (
              <Section title="SIGNAL PATTERNS" open={openMap.signalPatterns} onToggle={() => toggle("signalPatterns")}>
                <div className="signalPatternsGrid">
                  {safeList(report.signal_patterns).map((p, idx) => (
                    <div className="signalPatternCard" key={idx}>
                      <div className="signalPatternHeader"><div className="signalPatternName">{p.pattern_name}</div></div>
                      <div className="signalPatternBody">
                        <div className="signalPatternDesc">{p.description}</div>
                        {safeList(p.repeated_phrases).length > 0 && <div><div className="signalPatternSectionLabel">REPEATED PHRASES</div><div className="signalPatternPhrases">{safeList(p.repeated_phrases).map((phrase, pIdx) => <div className="signalPatternPhrase" key={pIdx}>"{normalizeText(phrase)}"</div>)}</div></div>}
                        <div><div className="signalPatternSectionLabel">IMPACT</div><div className="signalPatternImpact">{p.impact}</div></div>
                      </div>
                    </div>
                  ))}
                </div>
              </Section>
            )}

            {/* ── CAMPAIGN DIAGNOSIS ── */}
            {safeList(report.campaign_diagnosis).length > 0 && (
              <Section title="WHERE MESSAGING CAN IMPROVE" open={openMap.diagnosis} onToggle={() => toggle("diagnosis")}>
                <div className="diagnosisGrid">
                  {safeList(report.campaign_diagnosis).map((d, idx) => (
                    <div className="diagnosisCard" key={idx}>
                      <div className="diagnosisHeader"><div className="diagnosisType">{d.failure_type}</div></div>
                      <div className="diagnosisBody">
                        <div className="diagnosisDefinition">{d.definition}</div>
                        {safeList(d.signals).length > 0 && <div><div className="diagnosisSectionLabel">SIGNALS THAT TRIGGERED THIS</div><div className="diagnosisSignals">{safeList(d.signals).map((sig, sIdx) => <div className="diagnosisSignal" key={sIdx}>"{normalizeText(sig)}"</div>)}</div></div>}
                        <div><div className="diagnosisSectionLabel">IMPLICATION</div><div className="diagnosisImplication">{d.implication}</div></div>
                      </div>
                    </div>
                  ))}
                </div>
              </Section>
            )}

            {/* ── MARKET INSIGHT ── */}
            {report.market_insight && <div className="marketInsightBox"><div className="marketInsightLabel">MARKET INSIGHT</div><div className="marketInsightText">{report.market_insight}</div></div>}

            {/* ── SIGNALS SUMMARY ── */}
            <Section title="SIGNALS SUMMARY" open={openMap.summary} onToggle={() => toggle("summary")}>
              <div className="bullets">
                {report.signals_summary && <div className="bullet">{report.signals_summary}</div>}
                {report.evidence_summary && <div className="bullet">{report.evidence_summary}</div>}
              </div>
            </Section>

            {/* ── SEMANTIC INTENT CLUSTERS ── */}
            <Section title="SEMANTIC INTENT CLUSTERS" open={openMap.clusters} onToggle={() => toggle("clusters")}>
              <div className="clusterList">
                {safeList(report.intent_clusters).sort((a, b) => {
                  const order = ["Problem Awareness", "Frustration", "Solution Exploration", "Decision"];
                  return order.indexOf(a.intent_stage) - order.indexOf(b.intent_stage);
                }).map((cluster, idx) => (
                  <div className="clusterCard" key={`${cluster.cluster_name}-${idx}`}>
                    <div className="clusterTop">
                      <div className="clusterName">{cluster.cluster_name || `Cluster ${idx + 1}`}</div>
                      <div className="clusterStage">{cluster.intent_stage || "STAGE"}</div>
                    </div>
                    <div><div className="miniLabel">WHAT THE USER IS REALLY THINKING</div><div className="miniCopy">{cluster.mindset}</div></div>
                    <div><div className="miniLabel">REPRESENTATIVE USER QUERIES</div><div className="queryList">{safeList(cluster.user_queries).map((q, qIdx) => <div className="queryPill" key={qIdx}>{q}</div>)}</div></div>
                    {safeList(cluster.real_customer_signals).length > 0 && <div><div className="miniLabel">REAL CUSTOMER SIGNALS</div><div className="queryList">{safeList(cluster.real_customer_signals).map((sig, sIdx) => <div className="signalPill" key={sIdx}>"{normalizeText(sig)}"</div>)}</div></div>}
                    {cluster.pattern_observed && <div><div className="miniLabel">PATTERN OBSERVED</div><div className="patternBox">{cluster.pattern_observed}</div></div>}
                    {cluster.why_this_matters && <div><div className="whyLabel">WHY THIS MATTERS</div><div className="whyBox">{cluster.why_this_matters}</div></div>}
                    {safeList(cluster.evidence_sources).length > 0 && <div><div className="miniLabel">EVIDENCE SOURCES</div><div className="sourceList">{safeList(cluster.evidence_sources).map((src, eIdx) => <div className="sourceBadge" key={eIdx}>{src}</div>)}</div></div>}
                    <div><div className="miniLabel">COMPETITOR MESSAGING</div><div className="miniCopy">{cluster.competitor_messaging}</div></div>
                    <div><div className="miniLabel">STRATEGIC GAP</div><div className="miniCopy">{cluster.strategic_gap}</div></div>
                    <div>
                      <div className="miniLabel">RECOMMENDED AD ANGLES</div>
                      <div className="adAngleGrid">
                        {safeList(cluster.ad_angles).map((angle, aIdx) => {
                          if (typeof angle === "string") return <div className="queryPill" key={aIdx}>{angle}</div>;
                          return (
                            <div className="adAngleCard" key={aIdx}>
                              <div className="adAngleName">{angle.angle_name}</div>
                              {angle.what_it_does && <div className="adAngleWhat">{angle.what_it_does}</div>}
                              {angle.lead_with && <div className="adAngleLead">"{normalizeText(angle.lead_with)}"</div>}
                              {safeList(angle.driven_by).length > 0 && <div className="adAngleSignals">{safeList(angle.driven_by).map((sig, sIdx) => <div className="adAngleSignal" key={sIdx}>"{normalizeText(sig)}"</div>)}</div>}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                    <div><div className="miniLabel">FUNNEL ROLE</div><div className="miniCopy">{cluster.funnel_role}</div></div>
                  </div>
                ))}
              </div>
            </Section>

            {/* ── EVIDENCE FOUND ── */}
            <Section title="EVIDENCE FOUND" open={openMap.evidence} onToggle={() => toggle("evidence")}>
              {Object.values(knownActivity).some(Boolean) && (
                <div className="bullets" style={{ marginBottom: 14 }}>
                  {knownActivity.ctv && <div className="bullet">Known activity override: CTV / TV confirmed active.</div>}
                  {knownActivity.longForm && <div className="bullet">Known activity override: Long-form / DRTV confirmed active.</div>}
                  {knownActivity.youtube && <div className="bullet">Known activity override: YouTube / video ads confirmed active.</div>}
                  {knownActivity.podcasts && <div className="bullet">Known activity override: Podcasts / audio sponsorships confirmed active.</div>}
                </div>
              )}
              <div className="bullets" style={{ marginBottom: 14 }}>
                {safeList(evidence?.notes).filter(n => !ACTIVITY_PHRASES.some(phrase => n.toLowerCase().includes(phrase))).map((item, idx) => <div className="bullet" key={idx}>{item}</div>)}
              </div>
              <div className="signals" style={{ marginBottom: 14 }}>
                <div className="signalBox"><div className="signalLabel">CTV CONFIDENCE</div><div className="signalValue">{evidence?.ctvConfidence || "NONE"}</div></div>
                <div className="signalBox"><div className="signalLabel">LONG-FORM CONFIDENCE</div><div className="signalValue">{evidence?.longFormConfidence || "NONE"}</div></div>
                <div className="signalBox"><div className="signalLabel">YOUTUBE AD CONFIDENCE</div><div className="signalValue">{evidence?.youtubeAdConfidence || "NONE"}</div></div>
              </div>
              <div className="evidenceList">
                {safeList(evidence?.evidenceItems).length ? safeList(evidence?.evidenceItems).map((item, idx) => (
                  <div className="evidenceCard" key={`${item.url}-${idx}`}>
                    <div className="evidenceTop">
                      <div className="evidenceType">{String(item.type || "").toUpperCase()}</div>
                      <div className="evidenceBadge">{item.confidence || "LOW"}</div>
                    </div>
                    <div className="evidenceTitle">{item.title}</div>
                    <div className="evidenceSnippet">{item.snippet}</div>
                    <div style={{ marginTop: 8 }}><a className="evidenceLink" href={item.url} target="_blank" rel="noreferrer">{item.url}</a></div>
                  </div>
                )) : <div className="bullet">No public evidence items surfaced in this run.</div>}
              </div>
            </Section>

            {/* ── SCRAPED BRAND SIGNALS ── */}
            <Section title="SCRAPED BRAND SIGNALS" open={openMap.signals} onToggle={() => toggle("signals")}>
              {signals && (
                <div className="signals">
                  <div className="signalBox"><div className="signalLabel">DOMAIN</div><div className="signalValue">{signals.domain}</div></div>
                  <div className="signalBox"><div className="signalLabel">FORMS</div><div className="signalValue">{signals.formCount}</div></div>
                  <div className="signalBox"><div className="signalLabel">VIDEO EMBEDS</div><div className="signalValue">{signals.videoEmbedCount}</div></div>
                  <div className="signalBox"><div className="signalLabel">TESTIMONIALS PRESENT</div><div className="signalValue">{signals.testimonialsPresent ? "YES" : "NO"}</div></div>
                  <div className="signalBox"><div className="signalLabel">PRICING PRESENT</div><div className="signalValue">{signals.pricingPresent ? "YES" : "NO"}</div></div>
                  <div className="signalBox"><div className="signalLabel">SCHEMA PRESENT</div><div className="signalValue">{signals.schemaPresent ? "YES" : "NO"}</div></div>
                  <div className="signalBox"><div className="signalLabel">SOCIAL PLATFORMS</div><div className="signalValue">{safeList(signals.socialPlatformsDetected).join(", ") || "NONE DETECTED"}</div></div>
                  <div className="signalBox"><div className="signalLabel">TITLE</div><div className="signalValue">{signals.title || "N/A"}</div></div>
                  <div className="signalBox"><div className="signalLabel">PRIMARY H1</div><div className="signalValue">{safeList(signals.h1s)[0] || "N/A"}</div></div>
                </div>
              )}
            </Section>

            {/* ── METHODOLOGY ── */}
            <Section title="METHODOLOGY" open={false} onToggle={() => {}}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                {[
                  ["WEBSITE SIGNAL EXTRACTION", "We scrape the brand's homepage and key landing pages to extract messaging signals — headlines, CTAs, value propositions, product positioning, pricing language, and trust indicators."],
                  ["GOOGLE INTENT SIGNALS", "We pull real search queries, People Also Ask questions, and related searches from Google to map the exact language customers use at each stage of the decision journey."],
                  ["REVIEW & SOCIAL SIGNAL ANALYSIS", "We analyze customer reviews from third-party platforms and social comments from YouTube and other channels to surface real complaint language, emotional patterns, and unmet expectations."],
                  ["PUBLIC EVIDENCE GATHERING", "We scan public sources — news, forums, competitor pages, and industry content — to identify market context, competitive messaging patterns, and strategic gaps the brand can exploit."],
                  ["SEMANTIC INTENT CLUSTERING", "All signals are structured into intent clusters mapped across four stages: Problem Awareness, Frustration, Solution Exploration, and Decision."],
                  ["OUTPUT & APPLICATION", "Clusters are designed for direct use in ad strategy, creative briefing, landing page development, and campaign sequencing."],
                ].map(([label, text]) => (
                  <div key={label} style={{ border: "1px solid var(--border)", background: "#fff", padding: "14px 16px" }}>
                    <div style={{ fontFamily: "var(--mono)", fontSize: "0.52rem", letterSpacing: "0.18em", color: "var(--blue)", textTransform: "uppercase", marginBottom: 8 }}>{label}</div>
                    <div style={{ fontSize: "0.82rem", lineHeight: 1.65, color: "var(--muted)" }}>{text}</div>
                  </div>
                ))}
              </div>
            </Section>

          </div>
        )}
      </div>
    </div>
  );
}